import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { trackSuggestionAccepted } from './utils/analytics';
import {
  useCollaborativeEditor,
  ydocToJSON,
  encodeDocAnchor,
  type ScriptMentionItem,
  isSelectionInScriptScene,
  getSceneRange,
  SCRIPT_MODE_SETTING_REFRESH_META,
} from '@narrativebench/editor-core';
import { nextScriptModeSessionState } from './utils/script-mode-session';
import { EditorContent } from '@tiptap/react';
import { Node as ProseMirrorNode, Mark } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { useParams, Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useLocale } from './contexts/LocaleContext';
import { useToast } from './contexts/ToastContext';
import { useSettings } from './contexts/SettingsContext';
import { useFocusMode } from './contexts/FocusModeContext';
import { WS_BASE, API_BASE } from './config';
import { readJsonWithOnboarding } from './onboarding/notify-onboarding-update';
import {
  APP_VIEWPORT_HEIGHT,
  PANEL_BODY_FILL_CLASSNAME,
  appViewportHeightMinus,
  projectPanelPadding,
} from './constants/layout';
import { offlineFetch } from './utils/offline-fetch';
import { offlineQueue } from './utils/offline-queue';
import { getRandomNovelOpening } from './data/novel-openings';
import ReactMarkdown from 'react-markdown';
import { Expand, Minimize, Search, PanelRight } from 'lucide-react';
import { Button } from './components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './components/ui/tooltip';
import { FormattingBar } from './components/FormattingBar';
import { uploadImageResourceToS3 } from './utils/imageResources';
import { CommentModal } from './components/CommentModal';
import { DocumentLoadingPanel } from './components/DocumentLoadingPanel';
import { StyleCalibrationModal } from './components/StyleCalibrationModal';
import { SynonymsPopup } from './components/SynonymsPopup';
import { IconPicker } from './components/IconPicker';
import { EditorRightSidebar, EditorRightSidebarProvider } from './components/EditorRightSidebar';
import { type MainAreaView, useRightSidebar } from './contexts/RightSidebarContext';
import type { Editor as TiptapEditor } from '@tiptap/core';
import { EditorStatsOverlay } from './components/EditorStatsOverlay';
import { SidebarInset, useSidebar } from './components/ui/sidebar';
import { EditableDocumentHeader } from './components/EditableDocumentHeader';
import {
  COLOR_SUGGESTION_STRENGTH,
  COLOR_SUGGESTION_ORTHOGRAPHIC,
  COLOR_SUGGESTION_STYLE,
  COLOR_SUGGESTION_USER_EDIT_ALPHA_30,
  COLOR_SUCCESS_ALPHA_30,
  COLOR_DANGER_ALPHA_30,
  COLOR_WARNING_ALPHA_30,
  SCRIBANE_BLACK,
} from './colors';
import { DocumentHeader } from './components/DocumentHeader';
import { ViewHeader } from './components/ViewHeader';
import { Chapters } from './components/Chapters';
import { Storylines } from './components/Storylines';
import { Scenes } from './components/Scenes';
import { Characters } from './components/Characters';
import { Locations } from './components/Locations';
import { TechnicalSheet } from './components/TechnicalSheet';
import { ExportDocument } from './components/ExportDocument';
import { ShareDocument } from './components/ShareDocument';
import { VersionHistory } from './components/VersionHistory';
import { DocumentStats } from './components/DocumentStats';
import { WritingSettings } from './components/WritingSettings';
import { SuggestionsPanelHeaderActions } from './components/SuggestionsPanelHeaderActions';
import { Comments } from './components/Comments';
import { DocumentChat } from './components/DocumentChat';
import { FloatingClosePanelButton } from './components/FloatingClosePanelButton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './components/ui/tabs';
import { findTextPosition, findSuggestionTextPosition, sortPendingSuggestions, sortPendingSuggestionsByDocumentOrder, selectFirstNWordsExpandingToSentence, findScrollContainer, scrollToComment as scrollToCommentUtil, scrollToSuggestionMark, applyEditorScrollState, deferEditorCommand, deferAfterDialogExit, waitForDialogExit, isEditorScrollContainerReadyForRestore, highlightComments, clearSearchHighlights, recalculateCommentPosition, replaceRangePreservingMarks, getMountedEditorDom, syncSuggestionInsertMarkers, isInsertionSuggestion, restoreEditorTextSelection, handleMouseDownBelowEditorContent, isPointerBelowEditorContent, focusEditorAtEndWithoutScrolling } from './utils/editor-utils';
import { getCommentLocationLostToastMessage } from './utils/comment-location-feedback';
import { getDocumentMarkdownText, getDocumentPlainText } from './utils/document-plain-text';
import { stripInlineMarkdown } from './utils/inline-markdown';
import {
  getFeedbackAnalyzeDisableReason,
  getFeedbackAnalyzeDisableReasonFromWordCounts,
  getFeedbackAnalyzeDisableTooltip,
} from './utils/feedback-analyze-availability';
import { findSectionEndPos } from './utils/document-section';
import { extractTocEntries, type TocEntry } from './utils/extract-toc-entries';
import { calculateReadingTime, countWords, estimatePages } from './utils/word-count';
import { HeadingWithSectionMenu } from './extensions/heading-with-section-menu';
import { ParagraphSectionMenu } from './components/ParagraphSectionMenu';
import { DocumentSpeechPlaybackBar } from './components/DocumentSpeechPlaybackBar';
import { SceneHeadingScriptInfo } from './components/SceneHeadingScriptInfo';
import { BlockSectionScope } from './utils/block-section-scope-highlight';
import {
  DocumentSpeechHighlight,
  useDocumentSpeechHighlight,
} from './utils/document-speech-highlight';
import {
  emptyHeadingSectionReferences,
  HeadingSectionMenuProvider,
  type HeadingSectionMenuContextValue,
  type HeadingSectionReferences,
  type ManualChapterMenuOption,
} from './contexts/HeadingSectionMenuContext';
import {
  findEffectiveManualChapterIdForHeading,
  findUserLinkedManualChapterIdForHeading,
  getRelatedEntitiesFor,
  type ChapterWithManualMatch,
  type EntityReferenceEdge,
  type HeadingManualChapterLink,
} from './utils/related-entities';
import type { MentionableEntity } from './components/RichTextField';
import { useCreateEntityFromMentionOptional } from './contexts/CreateEntityFromMentionContext';
import {
  createEntityMentionExtension,
  MENTION_ADD_NEW_LABEL_KEYS,
  resolveScriptAwareMentionNodeType,
} from './utils/entity-mention-extension';
import { mergeMentionableEntities } from './utils/mention-suggestions';
import { ENTITY_TYPE_LABEL_KEYS } from './utils/entity-utils';
import { MobileAnalysisProgressToast } from './components/MobileAnalysisProgressToast';
import { updateFavicon } from './utils/favicon';
import { FloatingTocButton } from './components/FloatingTocButton';
import { ScriptModeFloatingButton } from './components/ScriptModeFloatingButton';
import { FloatingSearchButton } from './components/FloatingSearchButton';
import { FloatingToc } from './components/FloatingToc';
import { OfflineIndicator } from './components/OfflineIndicator';
import { SidebarCollapseHandler } from './components/SidebarCollapseHandler';
import {
  getDocumentPath,
  getDocumentPanelPath,
  panelSegmentToMainAreaView,
  mainAreaViewToPanelSegment,
  isValidPanelSegment,
} from './utils/document-panel-paths';
import { getProjectSectionIcon, getDocumentSectionIcon, getDocumentSectionLabelKey } from './utils/concept-icons';
import { SuggestionCard, isTemporarySuggestionId } from './components/SuggestionCard';
import { SuggestionNotFoundModal } from './components/SuggestionNotFoundModal';
import { CommentHoverCard } from './components/CommentHoverCard';
import { SuggestionHoverCard } from './components/SuggestionHoverCard';
import { useCommentHover } from './hooks/use-comment-hover';
import { useSuggestionHover } from './hooks/use-suggestion-hover';
import { usePersistedTocPinned } from './hooks/usePersistedTocPinned';
import { SearchBox } from './components/SearchBox';
import { useCreditCheck } from './hooks/useCreditCheck';
import { mapMentionableEntitiesFromApiResponse } from './utils/mentionable-entities';
import { MENTIONABLE_ENTITIES_CHANGED_EVENT } from './utils/mentionable-entities-events';
import { getReadingStateStorageKey, readingStateSignature, parseReadingState, type ReadingState } from '@narrativebench/editor-core';
import {
  createDebouncedReadingStateSaver,
  loadReadingStateForDocument,
  writeReadingStateToSessionStorage,
  type DebouncedReadingStateSaver,
} from './utils/editor-reading-state-sync';
import {
  clearPendingSectionScroll,
  findActiveTocItemPos,
  findActiveTocItemPosAtDocPos,
  findTocHeadingPosition,
  parseSectionScrollFromSearchParams,
  readPendingSectionScroll,
  stripSectionScrollSearchParams,
  TOC_ACTIVE_VIEWPORT_OFFSET_PX,
  TOC_ACTIVE_VIEWPORT_SLOP_PX,
} from './utils/document-section-navigation';

const EDITOR_IDLE_WORK_DEBOUNCE_MS = 150;

const scheduleTrailingTimeout = (
  timeoutRef: { current: ReturnType<typeof setTimeout> | null },
  callback: () => void,
  delayMs: number,
): void => {
  if (timeoutRef.current !== null) {
    clearTimeout(timeoutRef.current);
  }
  timeoutRef.current = setTimeout(() => {
    timeoutRef.current = null;
    callback();
  }, delayMs);
};

const HeadingSectionMenuContextSetup: React.FC<{
  editor: TiptapEditor | null;
  isAnalyzing: boolean;
  onAnalyzeSection: (from: number, to: number) => void;
  onMoveToNote: ((content: string) => Promise<{ projectId: string } | null>) | null;
  projectId: string | null;
  documentId: string | null;
  children: React.ReactNode;
}> = ({ editor, isAnalyzing, onAnalyzeSection, onMoveToNote, projectId, documentId, children }) => {
  const rightSidebar = useRightSidebar();
  const { setIsFocusMode } = useFocusMode();
  const { token } = useAuth();
  const { t } = useLocale();
  const { showErrorToast } = useToast();
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [entityRefs, setEntityRefs] = useState<EntityReferenceEdge[]>([]);
  const [documentChapters, setDocumentChapters] = useState<ChapterWithManualMatch[]>([]);
  const [headingManualChapterLinks, setHeadingManualChapterLinks] = useState<HeadingManualChapterLink[]>([]);
  const [manualChapters, setManualChapters] = useState<ManualChapterMenuOption[]>([]);
  const [chapterRefsReloadNonce, setChapterRefsReloadNonce] = useState(0);

  useEffect(() => {
    if (!token || !projectId || projectId === 'new' || !documentId || documentId === 'new') {
      setMentionableEntities([]);
      setEntityRefs([]);
      setDocumentChapters([]);
      setHeadingManualChapterLinks([]);
      setManualChapters([]);
      return;
    }

    const abortController = new AbortController();

    const load = async (): Promise<void> => {
      try {
        const [mentionableRes, refsRes, chaptersRes] = await Promise.all([
          fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal,
          }),
          fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal,
          }),
          fetch(`${API_BASE}/api/documents/${documentId}/chapters`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal,
          }),
        ]);

        if (abortController.signal.aborted) {
          return;
        }

        if (mentionableRes.ok) {
          const data = await mentionableRes.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }

        if (refsRes.ok) {
          const data = await refsRes.json();
          setEntityRefs(Array.isArray(data.references) ? data.references : []);
        } else {
          setEntityRefs([]);
        }

        if (chaptersRes.ok) {
          const data = await chaptersRes.json();
          const chaptersByLevel = (data.chaptersByLevel || {}) as Record<
            string,
            Array<ChapterWithManualMatch & { id?: string; order?: number }>
          >;
          const chapters = Object.values(chaptersByLevel).flat();
          setDocumentChapters(chapters);
          setHeadingManualChapterLinks(
            Array.isArray(data.headingManualChapterLinks) ? data.headingManualChapterLinks : [],
          );
          setManualChapters(
            chapters
              .filter((chapter): chapter is ChapterWithManualMatch & { id: string; order?: number } =>
                Boolean(chapter.isManual && chapter.id),
              )
              .map((chapter) => ({
                id: chapter.id,
                title: chapter.title,
                order: chapter.order,
              }))
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
          );
        } else {
          setDocumentChapters([]);
          setHeadingManualChapterLinks([]);
          setManualChapters([]);
        }
      } catch {
        if (!abortController.signal.aborted) {
          setMentionableEntities([]);
          setEntityRefs([]);
          setDocumentChapters([]);
          setHeadingManualChapterLinks([]);
          setManualChapters([]);
        }
      }
    };

    void load();

    return () => {
      abortController.abort();
    };
  }, [token, projectId, documentId, chapterRefsReloadNonce]);

  useEffect(() => {
    const handler = (event: Event): void => {
      const pid = (event as CustomEvent<{ projectId?: string }>).detail?.projectId;
      if (pid && projectId && pid === projectId) {
        setChapterRefsReloadNonce((n) => n + 1);
      }
    };
    window.addEventListener(MENTIONABLE_ENTITIES_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener(MENTIONABLE_ENTITIES_CHANGED_EVENT, handler);
    };
  }, [projectId]);

  const getEffectiveManualChapterIdForHeading = useCallback(
    (headingTitle: string): string | null =>
      findEffectiveManualChapterIdForHeading(
        headingTitle,
        documentChapters,
        headingManualChapterLinks,
      ),
    [documentChapters, headingManualChapterLinks],
  );

  const hasUserLinkedManualChapter = useCallback(
    (headingTitle: string): boolean =>
      Boolean(findUserLinkedManualChapterIdForHeading(headingTitle, headingManualChapterLinks)),
    [headingManualChapterLinks],
  );

  const getChapterReferencesForHeading = useCallback(
    (headingTitle: string): HeadingSectionReferences => {
      if (!documentId || documentId === 'new') {
        return emptyHeadingSectionReferences();
      }
      const chapterId = getEffectiveManualChapterIdForHeading(headingTitle);
      if (!chapterId) {
        return emptyHeadingSectionReferences();
      }
      return getRelatedEntitiesFor(
        'manual_chapter',
        chapterId,
        documentId,
        entityRefs,
        mentionableEntities,
      );
    },
    [documentId, getEffectiveManualChapterIdForHeading, entityRefs, mentionableEntities],
  );

  const linkHeadingToManualChapter = useCallback(
    async (headingTitle: string, manualChapterId: string): Promise<boolean> => {
      if (!token || !documentId || documentId === 'new') {
        return false;
      }
      try {
        const res = await fetch(`${API_BASE}/api/documents/${documentId}/heading-manual-chapter-link`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ headingTitle, manualChapterId }),
        });
        if (!res.ok) {
          showErrorToast(t('failed_to_link_chapter'));
          return false;
        }
        const data = await res.json();
        setHeadingManualChapterLinks(
          Array.isArray(data.headingManualChapterLinks) ? data.headingManualChapterLinks : [],
        );
        return true;
      } catch {
        showErrorToast(t('failed_to_link_chapter'));
        return false;
      }
    },
    [token, documentId, showErrorToast, t],
  );

  const unlinkHeadingFromManualChapter = useCallback(
    async (headingTitle: string): Promise<boolean> => {
      if (!token || !documentId || documentId === 'new') {
        return false;
      }
      try {
        const res = await fetch(
          `${API_BASE}/api/documents/${documentId}/heading-manual-chapter-link/remove`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ headingTitle }),
          },
        );
        if (!res.ok) {
          showErrorToast(t('failed_to_unlink_chapter'));
          return false;
        }
        const data = await res.json();
        setHeadingManualChapterLinks(
          Array.isArray(data.headingManualChapterLinks) ? data.headingManualChapterLinks : [],
        );
        return true;
      } catch {
        showErrorToast(t('failed_to_unlink_chapter'));
        return false;
      }
    },
    [token, documentId, showErrorToast, t],
  );

  const value = useMemo(
    (): HeadingSectionMenuContextValue => ({
      onAnalyzeSection,
      onChatAboutSection: (from: number, to: number): void => {
        if (!editor) {
          return;
        }
        const text = getDocumentMarkdownText(editor.state.doc, from, to);
        setIsFocusMode(false);
        rightSidebar.openChat({ selection: { from, to, text } });
      },
      onMoveToNote,
      isAnalyzing,
      projectId,
      documentId,
      manualChapters,
      getChapterReferencesForHeading,
      getEffectiveManualChapterIdForHeading,
      hasUserLinkedManualChapter,
      linkHeadingToManualChapter,
      unlinkHeadingFromManualChapter,
    }),
    [
      editor,
      isAnalyzing,
      onAnalyzeSection,
      onMoveToNote,
      projectId,
      documentId,
      manualChapters,
      getChapterReferencesForHeading,
      getEffectiveManualChapterIdForHeading,
      hasUserLinkedManualChapter,
      linkHeadingToManualChapter,
      unlinkHeadingFromManualChapter,
      rightSidebar,
      setIsFocusMode,
    ],
  );

  return (
    <HeadingSectionMenuProvider value={value}>
      {children}
    </HeadingSectionMenuProvider>
  );
};

const MainAreaDocumentChat: React.FC<{
  editor: TiptapEditor;
  documentId: string | null;
  shareToken: string | null;
}> = ({ editor, documentId, shareToken }) => {
  const { chatFocusRequest } = useRightSidebar();

  return (
    <DocumentChat
      editor={editor}
      documentId={documentId}
      shareToken={shareToken}
      isVisible={true}
      hideHeading={true}
      focusRequest={chatFocusRequest}
    />
  );
};

const RightSidebarToggleButton: React.FC = () => {
  const { t } = useLocale();
  let rightSidebar;
  try {
    rightSidebar = useRightSidebar();
  } catch {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={rightSidebar.toggleSidebar}
      className="h-10 w-10"
      aria-label={t('toggle_sidebar')}
      title={t('toggle_sidebar')}
    >
      <PanelRight className="h-10 w-10" style={{ color: SCRIBANE_BLACK }} />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
};

const Editor: React.FC = () => {
  const { projectId, documentId, panel: panelFromPath } = useParams<{
    projectId?: string;
    documentId: string;
    panel?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCharacterId = searchParams.get('characterId');
  const urlLocationId = searchParams.get('locationId');
  const urlChapterId = searchParams.get('chapterId');
  const urlStorylineId = searchParams.get('storylineId');
  const urlSceneId = searchParams.get('sceneId');

  const mainAreaViewFromPath =
    panelFromPath && isValidPanelSegment(panelFromPath)
      ? (panelSegmentToMainAreaView(panelFromPath) as MainAreaView)
      : null;
  const mainAreaViewFromSearch = ((): MainAreaView => {
    const panel = searchParams.get('panel');
    if (panel === 'chapter-outline') return 'chapters';
    if (panel === 'storylines') return 'storylines';
    if (panel === 'scenes') return 'scenes';
    if (panel === 'sharing') return 'share';
    if (panel === 'characters') return 'characters';
    if (panel === 'locations') return 'locations';
    if (panel === 'suggestions') return 'suggestions';
    if (panel === 'comments') return 'comments';
    if (panel === 'chat') return 'chat';
    return null;
  })();
  const mainAreaView: MainAreaView = mainAreaViewFromPath ?? mainAreaViewFromSearch ?? null;

  const documentBasePath = useMemo(() => {
    if (!documentId || documentId === 'new') return '';
    if (location.pathname.startsWith('/admin')) {
      return `/admin/documents/${documentId}`;
    }
    return getDocumentPath(projectId, documentId);
  }, [documentId, projectId, location.pathname]);

  const handleSelectMainAreaView = useCallback(
    (view: MainAreaView) => {
      if (!documentId || documentId === 'new') return;
      if (view) {
        const segment = mainAreaViewToPanelSegment(view);
        navigate(segment ? `${documentBasePath}/${segment}` : documentBasePath);
      } else {
        navigate(documentBasePath);
      }
    },
    [navigate, documentId, documentBasePath]
  );
  const handleCloseMainArea = useCallback(() => {
    if (!documentId || documentId === 'new') return;
    navigate(documentBasePath);
  }, [navigate, documentId, documentBasePath]);
  const { token, loading: authLoading, user } = useAuth();
  const { writingSettings, fetchHierarchicalWritingSettings } = useSettings();
  const { t } = useLocale();
  const createEntityFromMention = useCreateEntityFromMentionOptional();
  let sidebar;
  try {
    sidebar = useSidebar();
  } catch {
    sidebar = null;
  }
  let rightSidebar;
  try {
    rightSidebar = useRightSidebar();
  } catch {
    rightSidebar = null;
  }
  const { toast, variant: toastVariant, showToast, showErrorToast, hideToast } = useToast();
  const { checkCredits, showCreditLimitModal, CreditLimitModalComponent } = useCreditCheck();
  const [masterKey] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('masterKey');
  });
  const [shareToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('shareToken');
  });
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [documentIcon, setDocumentIcon] = useState<string | null>(null);
  const [documentProjectId, setDocumentProjectId] = useState<string | null>(null);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [mentionableEntitiesReloadNonce, setMentionableEntitiesReloadNonce] = useState(0);
  const [documentOwnerId, setDocumentOwnerId] = useState<string | null>(null);
  const [canManageSharing, setCanManageSharing] = useState<boolean>(false);
  const [projectTitle, setProjectTitle] = useState<string>('');
  const [projectMainDocumentIcon, setProjectMainDocumentIcon] = useState<string | null>(null);
  const [projectMainDocumentId, setProjectMainDocumentId] = useState<string | null>(null);
  const [projectDocuments, setProjectDocuments] = useState<
    Array<{ id: string; title: string; icon: string | null; pinned?: boolean }>
  >([]);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const [feedback, setFeedback] = useState<string>('');
  const [isLoadingFeedback, setIsLoadingFeedback] = useState<boolean>(false);
  const [feedbackData, setFeedbackData] = useState<{
    issues: Array<{
      text: string;
      type: 'orthographic' | 'other' | 'strength';
      feedback: string;
      suggestion?: string;
    }>;
  } | null>(null);
  const [wordCount, setWordCount] = useState<number>(0);
  const [readingTime, setReadingTime] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(0);
  const [selectedTextWordCount, setSelectedTextWordCount] = useState<number>(0);
  const [selectedTextReadingTime, setSelectedTextReadingTime] = useState<number>(0);
  const [selectedTextPageCount, setSelectedTextPageCount] = useState<number>(0);
  const [comments, setComments] = useState<Array<{
    id: string;
    documentId: string;
    userId: string;
    userEmail?: string;
    content: string;
    fromPos: number;
    toPos: number;
    fromRel?: Record<string, unknown> | null;
    toRel?: Record<string, unknown> | null;
    selectedText: string;
    createdAt: string;
    updatedAt: string;
    replies?: Array<{
      id: string;
      userId: string;
      userEmail?: string;
      createdAt: string;
      comment: string;
    }>;
  }>>([]);
  const commentsRef = useRef(comments);
  commentsRef.current = comments;
  const [suggestions, setSuggestions] = useState<Array<{
    id: string;
    userId: string;
    userEmail?: string;
    documentId: string | null;
    originalText: string;
    suggestionText: string;
    issueType: 'orthographic' | 'preciseness' | 'clarity' | 'consistency' | 'orthographic_consistency' | 'grammar' | 'spelling' | 'punctuation' | 'sentence_structure' | 'repetition' | 'overused_words' | 'cacophony' | 'passive_voice' | 'show_vs_tell' | 'pacing' | 'adverbs' | 'adjectives' | 'cliches' | 'transitions' | 'style' | 'strength' | 'user_edit';
    feedbackText: string;
    llmCallId: string | null;
    llmCall?: {
      id: string;
      userId: string | null;
      documentId: string | null;
      triggerSource: string;
      provider: 'gemini' | 'openai';
      model: string;
      prompt: string;
      response: string | null;
      promptTokens: number | null;
      responseTokens: number | null;
      costUsd: number | null;
      createdAt: string;
    } | null;
    languageCode: string | null;
    languageName: string | null;
    shownAt: string;
    status: 'pending' | 'applied' | 'rejected' | 'reported';
    closedAt: string | null;
    createdAt: string;
    scopeFromPos?: number | null;
    scopeToPos?: number | null;
    scopeSelectedText?: string | null;
    fromPos?: number | null;
    toPos?: number | null;
    fromRel?: Record<string, unknown> | null;
    toRel?: Record<string, unknown> | null;
    contextBefore?: string | null;
    contextAfter?: string | null;
  }>>([]);
  const suggestionsRef = useRef(suggestions);
  suggestionsRef.current = suggestions;
  const [isCommentModalOpen, setIsCommentModalOpen] = useState<boolean>(false);
  const isCommentModalOpenRef = useRef(isCommentModalOpen);
  isCommentModalOpenRef.current = isCommentModalOpen;
  const [isCreatingComment, setIsCreatingComment] = useState<boolean>(false);
  const [selectedTextForComment, setSelectedTextForComment] = useState<string>('');
  const [isStyleCalibrationModalOpen, setIsStyleCalibrationModalOpen] = useState<boolean>(false);
  const [pendingStyleCalibrationText, setPendingStyleCalibrationText] = useState<string | null>(null);
  const [suggestionNotFound, setSuggestionNotFound] = useState<typeof suggestions[0] | null>(null);
  const [pendingStyleCalibrationSelectionRange, setPendingStyleCalibrationSelectionRange] = useState<{ from: number; to: number } | null>(null);
  const [pendingStyleCalibrationPendingCount, setPendingStyleCalibrationPendingCount] = useState<number>(0);
  const [selectedRangeForComment, setSelectedRangeForComment] = useState<{ from: number; to: number } | null>(null);
  const [isSynonymsPopupOpen, setIsSynonymsPopupOpen] = useState<boolean>(false);
  const isSynonymsPopupOpenRef = useRef(isSynonymsPopupOpen);
  isSynonymsPopupOpenRef.current = isSynonymsPopupOpen;
  const [synonyms, setSynonyms] = useState<string[]>([]);
  const [isLoadingSynonyms, setIsLoadingSynonyms] = useState<boolean>(false);
  const [synonymsPopupPosition, setSynonymsPopupPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [selectedRangeForSynonyms, setSelectedRangeForSynonyms] = useState<{ from: number; to: number } | null>(null);
  const [synonymsWordCount, setSynonymsWordCount] = useState<number>(1);
  const [, setIsEditorFocused] = useState<boolean>(false);
  const [isEmpty, setIsEmpty] = useState<boolean>(true);
  const [tocHeadings, setTocHeadings] = useState<TocEntry[]>([]);
  const [tocParagraphs, setTocParagraphs] = useState<Array<{ text: string; pos: number }>>([]);
  const [draggedTocIndex, setDraggedTocIndex] = useState<number | null>(null);
  const [dragOverTocIndex, setDragOverTocIndex] = useState<number | null>(null);
  const [touchDraggedIndex, setTouchDraggedIndex] = useState<number | null>(null);
  const [touchDragOverIndex, setTouchDragOverIndex] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const tocListRef = useRef<HTMLUListElement | null>(null);
  const [isFloatingTocVisible, setIsFloatingTocVisible] = useState<boolean>(false);
  const [isTocPinned, setIsTocPinned] = usePersistedTocPinned(documentId);
  const [activeTocPos, setActiveTocPos] = useState<number | null>(null);
  const ignoreTocScrollActiveUpdatesRef = useRef<boolean>(false);
  const [commentsMainPanelTab, setCommentsMainPanelTab] = useState<'pending' | 'deleted'>('pending');
  const [deletedComments, setDeletedComments] = useState<typeof comments>([]);
  const [commentsRefreshTrigger, setCommentsRefreshTrigger] = useState<number>(0);
  const { isFocusMode, setIsFocusMode } = useFocusMode();
  const isFocusModeRef = useRef(isFocusMode);
  isFocusModeRef.current = isFocusMode;
  const [isScriptModeActive, setIsScriptModeActive] = useState<boolean>(false);
  const [isSearchVisible, setIsSearchVisible] = useState<boolean>(false);
  const [isReplaceMode, setIsReplaceMode] = useState<boolean>(false);
  const [searchInitialText, setSearchInitialText] = useState<string>('');
  const isSearchVisibleRef = useRef(isSearchVisible);
  isSearchVisibleRef.current = isSearchVisible;

  useEffect(() => {
    if (!isSearchVisible) {
      setSearchInitialText('');
    }
  }, [isSearchVisible]);
  const editorWrapperRef = useRef<HTMLDivElement | null>(null);
  const editorScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const forceNewReviewRef = useRef(false);
  const suggestionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const previousConnectionErrorRef = useRef<string | null>(null);
  const showSuggestionsRef = useRef<(() => void) | null>(null);
  const showCommentsRef = useRef<(() => void) | null>(null);
  const refreshCommentsRef = useRef<(() => void) | null>(null);

  const shareTokenValidatedRef = useRef<boolean>(false);
  const titleFetchedRef = useRef<boolean>(false);
  const previousDocumentIdRef = useRef<string | undefined>(undefined);
  const focusModeEnabledForDocumentRef = useRef<string | undefined>(undefined);
  const previousMainAreaViewRef = useRef<MainAreaView | null>(null);
  const lastRestoredCaretDocumentIdRef = useRef<string | null>(null);
  const pendingScrollRestoreRef = useRef<ReadingState | null>(null);
  const isScrollRestoreCompleteRef = useRef(false);
  const readingStateSaverRef = useRef<DebouncedReadingStateSaver | null>(null);
  const loadedPositionSignatureRef = useRef<string | null>(null);
  const lastPersistedSignatureRef = useRef<string | null>(null);
  const [isPositionLoaded, setIsPositionLoaded] = useState(false);

  useEffect(() => {
    shareTokenValidatedRef.current = false;
    titleFetchedRef.current = false;
  }, [shareToken, documentId]);

  useEffect(() => {
    if (documentId && documentId !== 'new' && documentId !== previousDocumentIdRef.current) {
      if (typeof document !== 'undefined') {
        document.cookie = 'right_sidebar_state=false; path=/; max-age=604800';
      }
      previousDocumentIdRef.current = documentId;
    }
  }, [documentId]);

  useEffect(() => {
    lastRestoredCaretDocumentIdRef.current = null;
    isScrollRestoreCompleteRef.current = false;
    loadedPositionSignatureRef.current = null;
    setIsPositionLoaded(false);

    if (!documentId || documentId === 'new') {
      pendingScrollRestoreRef.current = null;
      isScrollRestoreCompleteRef.current = true;
      setIsPositionLoaded(true);
      return;
    }

    if (!shareToken && (authLoading || !token)) {
      return;
    }

    let cancelled = false;

    const loadPosition = async (): Promise<void> => {
      const canUseServer = Boolean(token && !shareToken);
      const merged = canUseServer
        ? await loadReadingStateForDocument(API_BASE, documentId, token)
        : parseReadingState(
            typeof sessionStorage !== 'undefined'
              ? sessionStorage.getItem(getReadingStateStorageKey(documentId))
              : null
          );

      if (cancelled) return;

      const signature = merged ? readingStateSignature(merged) : '';
      if (loadedPositionSignatureRef.current !== signature) {
        lastRestoredCaretDocumentIdRef.current = null;
        loadedPositionSignatureRef.current = signature;
      }

      pendingScrollRestoreRef.current = merged;
      lastPersistedSignatureRef.current = signature || null;
      setIsPositionLoaded(true);
    };

    void loadPosition();

    return () => {
      cancelled = true;
      void readingStateSaverRef.current?.flush();
    };
  }, [documentId, token, shareToken, authLoading]);

  useEffect(() => {
    if (!documentId || documentId === 'new' || !token || shareToken) {
      readingStateSaverRef.current?.cancel();
      readingStateSaverRef.current = null;
      return undefined;
    }

    readingStateSaverRef.current = createDebouncedReadingStateSaver(API_BASE, documentId, token);

    const flushOnLeave = (): void => {
      void readingStateSaverRef.current?.flush();
    };

    window.addEventListener('pagehide', flushOnLeave);
    window.addEventListener('beforeunload', flushOnLeave);

    return () => {
      window.removeEventListener('pagehide', flushOnLeave);
      window.removeEventListener('beforeunload', flushOnLeave);
      void readingStateSaverRef.current?.flush();
      readingStateSaverRef.current?.cancel();
      readingStateSaverRef.current = null;
    };
  }, [documentId, token, shareToken]);

  useEffect(() => {
    if (
      documentId &&
      documentId !== 'new' &&
      documentId !== focusModeEnabledForDocumentRef.current &&
      isMobile &&
      mainAreaView === null
    ) {
      setIsFocusMode(true);
      focusModeEnabledForDocumentRef.current = documentId;
    }
  }, [documentId, isMobile, mainAreaView, setIsFocusMode]);

  useEffect(() => {
    if (mainAreaView !== null) {
      setIsFocusMode(false);
    }
  }, [mainAreaView, setIsFocusMode]);

  useEffect(() => {
    return () => {
      focusModeEnabledForDocumentRef.current = undefined;
      setIsFocusMode(false);
    };
  }, [setIsFocusMode]);

  useEffect(() => {
    const validateShareToken = async (): Promise<void> => {
      if (!shareToken || !documentId || documentId === 'new') {
        setIsReadOnly(false);
        shareTokenValidatedRef.current = true;
        return;
      }

      if (shareTokenValidatedRef.current) {
        return;
      }

      shareTokenValidatedRef.current = true;

      try {
        const response = await fetch(`${API_BASE}/api/share-links/${shareToken}/validate`);
        if (response.ok) {
          const data = await response.json();
          if (data.valid) {
            setIsReadOnly(true);
            if (data.documentTitle) {
              setDocumentTitle(data.documentTitle);
              titleFetchedRef.current = true;
            }
            if (data.documentIcon) {
              setDocumentIcon(data.documentIcon);
            }
          } else {
            setIsReadOnly(false);
          }
        } else {
          setIsReadOnly(false);
        }
      } catch (err) {
        console.error('Error validating share token:', err);
        setIsReadOnly(false);
      }
    };

    const checkDocumentSharePermission = async (): Promise<void> => {
      if (!token || !documentId || documentId === 'new' || shareToken) {
        return;
      }

      try {
        const documentUrl = projectId && projectId !== 'new'
          ? `${API_BASE}/api/documents/${documentId}?contextProjectId=${encodeURIComponent(projectId)}`
          : `${API_BASE}/api/documents/${documentId}`;
        const response = await fetch(documentUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.ownerId) {
            setDocumentOwnerId(data.ownerId);
          }
          setCanManageSharing(Boolean(data.canManageSharing || (user && data.ownerId === user.id)));
          if (data.sharePermission) {
            if (data.sharePermission === 'reader') {
              setIsReadOnly(true);
            } else if (data.sharePermission === 'commenter' || data.sharePermission === 'editor') {
              setIsReadOnly(false);
            }
          }
        }
      } catch (err) {
        console.error('Error checking document share permission:', err);
      }
    };

    validateShareToken();
    checkDocumentSharePermission();
  }, [shareToken, documentId, token, user, projectId]);


  const initialEditable = shareToken ? false : true;
  const [editorEditable, setEditorEditable] = useState<boolean>(initialEditable);

  useEffect(() => {
    if (shareTokenValidatedRef.current) {
      setEditorEditable(!isReadOnly);
    }
  }, [isReadOnly]);

  const placeholderText = useMemo(() => {
    const browserLocale = typeof navigator !== 'undefined' ? navigator.language : 'en';
    return getRandomNovelOpening(browserLocale);
  }, []);
  const getPlaceholderText = useCallback((): string => placeholderText, [placeholderText]);

  const writingSettingsRef = useRef(writingSettings);
  useEffect(() => {
    writingSettingsRef.current = writingSettings;
  }, [writingSettings]);

  const getReplaceHyphensWithDashesSetting = useCallback(() => {
    return writingSettingsRef.current.replaceHyphensWithDashes;
  }, []);

  const getCollapseConsecutiveSpacesSetting = useCallback(() => {
    return writingSettingsRef.current.collapseConsecutiveSpaces;
  }, []);

  const getAutomaticReplacementsSetting = useCallback(() => {
    return writingSettingsRef.current.automaticReplacements || [];
  }, []);

  const getEnableListsSetting = useCallback(() => {
    return writingSettingsRef.current.enableLists;
  }, []);

  const getEnableScriptModeSetting = useCallback(() => {
    return writingSettingsRef.current.enableScriptMode;
  }, []);

  const isScriptModeActiveRef = useRef(false);
  const canAutoActivateScriptModeFromSceneRef = useRef(true);

  const getIsScriptModeActive = useCallback((): boolean => {
    if (!writingSettingsRef.current.enableScriptMode) {
      return false;
    }
    return isScriptModeActiveRef.current;
  }, []);

  const scriptMentionEntitiesRef = useRef<ScriptMentionItem[]>([]);
  const entityMentionEntitiesRef = useRef<MentionableEntity[]>([]);

  useEffect(() => {
    const projectIdToFetch = projectId || documentProjectId;
    if (!token || !projectIdToFetch || projectIdToFetch === 'new' || !documentId || documentId === 'new') {
      scriptMentionEntitiesRef.current = [];
      entityMentionEntitiesRef.current = [];
      setMentionableEntities([]);
      return;
    }

    const abortController = new AbortController();

    const load = async (): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectIdToFetch}/mentionable-entities`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal,
        });
        if (!res.ok) {
          scriptMentionEntitiesRef.current = [];
          entityMentionEntitiesRef.current = [];
          setMentionableEntities([]);
          return;
        }
        const data = await res.json();
        const all = mapMentionableEntitiesFromApiResponse(data);
        entityMentionEntitiesRef.current = all;
        setMentionableEntities(all);
        scriptMentionEntitiesRef.current = all
          .filter(
            (
              e,
            ): e is {
              id: string;
              label: string;
              type: 'character' | 'location';
              documentId?: string;
              documentTitle?: string;
            } => e.type === 'character' || e.type === 'location',
          )
          .map((e) => ({
            id: e.id,
            label: e.label,
            type: e.type,
            ...(e.documentId ? { documentId: e.documentId } : {}),
            ...(e.documentTitle ? { documentTitle: e.documentTitle } : {}),
          }));
      } catch {
        if (!abortController.signal.aborted) {
          scriptMentionEntitiesRef.current = [];
          entityMentionEntitiesRef.current = [];
          setMentionableEntities([]);
        }
      }
    };

    void load();

    return () => {
      abortController.abort();
    };
  }, [token, projectId, documentProjectId, documentId, mentionableEntitiesReloadNonce]);

  useEffect(() => {
    const handler = (e: Event): void => {
      const pid = (e as CustomEvent<{ projectId?: string }>).detail?.projectId;
      const projectIdToFetch = projectId || documentProjectId;
      if (pid && projectIdToFetch && pid === projectIdToFetch) {
        setMentionableEntitiesReloadNonce((n) => n + 1);
      }
    };
    window.addEventListener(MENTIONABLE_ENTITIES_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener(MENTIONABLE_ENTITIES_CHANGED_EVENT, handler);
    };
  }, [projectId, documentProjectId]);

  const getScriptMentionItems = useCallback(() => scriptMentionEntitiesRef.current, []);

  const createdEntitiesFromMention = createEntityFromMention?.createdEntities ?? [];
  const createdEntitiesFromMentionRef = useRef(createdEntitiesFromMention);
  createdEntitiesFromMentionRef.current = createdEntitiesFromMention;
  const createMentionRequest = createEntityFromMention?.request ?? null;
  const suppressEntityMentionSuggestionsRef = useRef(false);
  suppressEntityMentionSuggestionsRef.current = createMentionRequest != null;
  const openCreateEntityRef = useRef(createEntityFromMention?.openCreateEntity);
  openCreateEntityRef.current = createEntityFromMention?.openCreateEntity;
  const getCreateableTypesRef = useRef(createEntityFromMention?.getCreateableTypes);
  getCreateableTypesRef.current = createEntityFromMention?.getCreateableTypes;

  const entityMentionExtension = useMemo(
    () =>
      createEntityMentionExtension({
        getMentionableEntities: () =>
          mergeMentionableEntities(
            entityMentionEntitiesRef.current,
            createdEntitiesFromMentionRef.current,
          ),
        getTypeLabel: (type) =>
          type === 'user'
            ? t('shared_with')
            : t(ENTITY_TYPE_LABEL_KEYS[type as keyof typeof ENTITY_TYPE_LABEL_KEYS] ?? 'Documents'),
        getNoMatchLabel: () => t('mention_no_matching_entry'),
        getCreateLabel: (type) => t(MENTION_ADD_NEW_LABEL_KEYS[type]),
        getCreateableTypes: (fallbackDocumentId) => getCreateableTypesRef.current?.(fallbackDocumentId) ?? [],
        getCurrentDocumentId: () =>
          documentId && documentId !== 'new' ? documentId : undefined,
        openCreateEntity: (type, name, options) => openCreateEntityRef.current?.(type, name, options),
        shouldSuppressSuggestions: () => suppressEntityMentionSuggestionsRef.current,
        shouldAllowMentions: () => writingSettingsRef.current.enableMentions === true,
        resolveMentionNodeType: (entity) =>
          resolveScriptAwareMentionNodeType(
            entity,
            writingSettingsRef.current.enableScriptMode,
          ),
      }),
    [t, documentId],
  );

  const getPreferEntityMentions = useCallback((): boolean => {
    return writingSettingsRef.current.enableMentions === true;
  }, []);

  const blockSectionScopeExtensions = useMemo(
    () => [BlockSectionScope, DocumentSpeechHighlight],
    [],
  );

  const handleImageFiles = useCallback(
    async (files: File[]): Promise<string[]> => {
      if (!token) {
        return [];
      }

      const projectIdForUpload = projectId || documentProjectId;
      if (!projectIdForUpload) {
        return [];
      }

      const urls: string[] = [];

      for (const file of files) {
        if (!file.type || !file.type.startsWith('image/')) {
          // Skip non-image files defensively.
          // The FileHandler extension already filters, this is just an extra guard.
          // eslint-disable-next-line no-continue
          continue;
        }

        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve(typeof reader.result === 'string' ? reader.result : '');
          };
          reader.onerror = () => {
            reject(new Error('Failed to read image file'));
          };
          reader.readAsDataURL(file);
        });

        if (dataUrl) {
          urls.push(dataUrl);
        }

        uploadImageResourceToS3({
          file,
          projectId: projectIdForUpload,
          token,
          embed: true,
          documentId: documentId && documentId !== 'new' ? documentId : undefined,
        }).catch((error) => {
          const err = error as Error & { errorCode?: string };
          let message: string;
          if (err.errorCode === 'RESOURCE_LIMIT_REACHED') {
            message = t('resource_limit_reached_message') ?? err.message;
          } else if (err.errorCode === 'STORAGE_LIMIT_REACHED') {
            message = t('storage_limit_reached_message') ?? err.message;
          } else {
            message = t('failed_to_upload_image') ?? 'Failed to upload image';
          }
          showErrorToast(message);
        });
      }

      return urls;
    },
    [projectId, documentProjectId, token, documentId, showErrorToast, t],
  );

  const { editor, ydoc, isDocumentLoaded, isConnected, connectionError } = useCollaborativeEditor({
    documentId: documentId || 'new',
    token: shareToken ? '' : (token || ''),
    apiBase: API_BASE,
    websocketUrl: WS_BASE,
    masterKey: masterKey || undefined,
    userName: user?.email || undefined,
    editable: editorEditable,
    shareToken: shareToken || undefined,
    getReplaceHyphensWithDashesSetting,
    getCollapseConsecutiveSpacesSetting,
    getAutomaticReplacementsSetting,
    getEnableListsSetting,
    getEnableScriptModeSetting,
    getScriptMentionItems,
    getIsScriptModeActive,
    getPreferEntityMentions,
    getPlaceholderText,
    handleImageFiles,
    headingExtension: HeadingWithSectionMenu,
    entityMentionExtension,
    extraExtensions: blockSectionScopeExtensions,
    onStateless: (payload: string) => {
      if (payload === 'suggestions-changed') {
        void loadSuggestionsRef.current({ notifyOnNew: true });
      }
      if (payload === 'comments-changed') {
        void loadCommentsRef.current({ notifyOnNew: true });
      }
    },
  });

  useDocumentSpeechHighlight(editor);

  const openSuggestionsHandledRef = useRef(false);
  const openCommentIdHandledRef = useRef<string | null>(null);
  const [pendingEditorCommentScrollId, setPendingEditorCommentScrollId] = useState<string | null>(null);
  const loadSuggestionsRef = useRef<(options?: { sortByDocumentOrder?: boolean; notifyOnNew?: boolean }) => Promise<void>>(async () => {});
  const loadCommentsRef = useRef<(options?: { notifyOnNew?: boolean }) => Promise<void>>(async () => {});

  useEffect(() => {
    isScriptModeActiveRef.current = isScriptModeActive;
  }, [isScriptModeActive]);

  useEffect(() => {
    canAutoActivateScriptModeFromSceneRef.current = true;
    setIsScriptModeActive(false);
    openCommentIdHandledRef.current = null;
    setPendingEditorCommentScrollId(null);
  }, [documentId]);

  useEffect(() => {
    if (!writingSettings.enableScriptMode) {
      setIsScriptModeActive(false);
      canAutoActivateScriptModeFromSceneRef.current = true;
    }
  }, [writingSettings.enableScriptMode]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }
    editor.view.dispatch(editor.state.tr.setMeta(SCRIPT_MODE_SETTING_REFRESH_META, true));
  }, [editor, isDocumentLoaded, writingSettings.enableScriptMode]);

  useEffect(() => {
    if (!editor) {
      return;
    }
    const tryActivateScriptMode = (): void => {
      const next = nextScriptModeSessionState({
        enableScriptMode: writingSettingsRef.current.enableScriptMode,
        inScriptScene: isSelectionInScriptScene(editor.state),
        isActive: isScriptModeActiveRef.current,
        canAutoActivateFromScene: canAutoActivateScriptModeFromSceneRef.current,
      });
      canAutoActivateScriptModeFromSceneRef.current = next.canAutoActivateFromScene;
      if (next.isActive !== isScriptModeActiveRef.current) {
        setIsScriptModeActive(next.isActive);
      }
    };
    tryActivateScriptMode();
    editor.on('selectionUpdate', tryActivateScriptMode);
    return () => {
      editor.off('selectionUpdate', tryActivateScriptMode);
    };
  }, [editor, writingSettings.enableScriptMode]);

  const {
    hoveredComment,
    hoveredCommentPosition,
    commentHoverCardContentRef,
    setHoveredComment,
    closeCommentHover,
    setKeepCommentHoverOpen,
    onCommentHoverCardMouseEnter,
    onCommentHoverCardMouseLeave,
  } = useCommentHover(editor, comments, isMobile);

  const {
    hoveredSuggestion,
    hoveredSuggestionPosition,
    suggestionHoverCardContentRef,
    closeSuggestionHover,
    onSuggestionHoverCardMouseEnter,
    onSuggestionHoverCardMouseLeave,
  } = useSuggestionHover(editor, suggestions, isMobile);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!isReadOnly);
    }
  }, [editor, isReadOnly]);

  useEffect(() => {
    const proseMirrorElement = getMountedEditorDom(editor);
    if (!proseMirrorElement) {
      return;
    }

    const justification = writingSettings.textJustification;
    
    if (justification === 'none') {
      proseMirrorElement.style.textAlign = '';
    } else {
      proseMirrorElement.style.textAlign = justification;
    }

    const styleElement = document.createElement('style');
    styleElement.id = 'text-justification-style';

    const scriptCharacterCueCenterRule =
      '.ProseMirror .scribane-script-character-cue,' +
      '.ProseMirror .scribane-script-character-cue p,' +
      '.ProseMirror p.scribane-script-character-cue,' +
      '.ProseMirror .scribane-script-parenthetical,' +
      '.ProseMirror p.scribane-script-parenthetical{' +
      'text-align:center !important;' +
      '}';

    if (justification === 'none') {
      styleElement.textContent =
        `.ProseMirror p { text-align: initial !important; }` + scriptCharacterCueCenterRule;
    } else if (justification === 'justified') {
      styleElement.textContent =
        `.ProseMirror p { text-align: justify !important; }` + scriptCharacterCueCenterRule;
    } else {
      styleElement.textContent =
        `.ProseMirror p { text-align: ${justification} !important; }` + scriptCharacterCueCenterRule;
    }

    const existingStyle = document.getElementById('text-justification-style');
    if (existingStyle) {
      existingStyle.remove();
    }
    document.head.appendChild(styleElement);

    return () => {
      const styleToRemove = document.getElementById('text-justification-style');
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [editor, isDocumentLoaded, writingSettings.textJustification]);

  useEffect(() => {
    if (!token || !user || (!projectId && !documentId)) {
      return;
    }
    fetchHierarchicalWritingSettings(projectId || null, documentId || null);
    return () => {
      void fetchHierarchicalWritingSettings(null, null);
    };
  }, [token, user, projectId, documentId, fetchHierarchicalWritingSettings]);

  useEffect(() => {
    if (!documentId || documentId === 'new') {
      return;
    }

    const handleDocumentSettingsUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<{ documentId: string }>;
      if (customEvent.detail.documentId === documentId && token && user) {
        fetchHierarchicalWritingSettings(projectId || null, documentId || null);
      }
    };

    const handleProjectSettingsUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<{ projectId: string }>;
      if (projectId && customEvent.detail.projectId === projectId && token && user) {
        fetchHierarchicalWritingSettings(projectId || null, documentId || null);
      }
    };

    const handleUserSettingsUpdate = (): void => {
      if (token && user) {
        fetchHierarchicalWritingSettings(projectId || null, documentId || null);
      }
    };

    window.addEventListener('documentWritingSettingsUpdated', handleDocumentSettingsUpdate);
    window.addEventListener('projectWritingSettingsUpdated', handleProjectSettingsUpdate);
    window.addEventListener('userWritingSettingsUpdated', handleUserSettingsUpdate);

    return () => {
      window.removeEventListener('documentWritingSettingsUpdated', handleDocumentSettingsUpdate);
      window.removeEventListener('projectWritingSettingsUpdated', handleProjectSettingsUpdate);
      window.removeEventListener('userWritingSettingsUpdated', handleUserSettingsUpdate);
    };
  }, [documentId, projectId, token, user, fetchHierarchicalWritingSettings]);

  const isErrorToast = toastVariant === 'error';

  useEffect(() => {
    if (!authLoading && !token && !masterKey && !shareToken) {
      navigate('/login');
    }
  }, [authLoading, token, masterKey, shareToken, navigate]);



  useEffect(() => {
    const fetchDocumentTitle = async () => {
      if (shareToken && documentId && documentId !== 'new') {
        if (!shareTokenValidatedRef.current || !isReadOnly || titleFetchedRef.current) {
          return;
        }
        titleFetchedRef.current = true;
        try {
          const response = await fetch(`${API_BASE}/api/share-links/${shareToken}/document`);
          if (response.ok) {
            const data = await response.json();
            setDocumentTitle(data.title || '');
            setDocumentIcon(data.icon || null);
          }
        } catch (err) {
          console.error('Error fetching document title via share token:', err);
        }
        return;
      }

      if (shareToken) {
        return;
      }

      if ((!token && !masterKey) || !documentId || documentId === 'new') {
        if (!shareToken) {
          setDocumentTitle('');
        }
        return;
      }

      if (titleFetchedRef.current) {
        return;
      }

      titleFetchedRef.current = true;

      try {
        const url = masterKey 
          ? `${API_BASE}/api/documents/${documentId}?masterKey=${encodeURIComponent(masterKey)}`
          : projectId && projectId !== 'new'
            ? `${API_BASE}/api/documents/${documentId}?contextProjectId=${encodeURIComponent(projectId)}`
            : `${API_BASE}/api/documents/${documentId}`;
        const response = await fetch(url, {
          headers: token ? {
            Authorization: `Bearer ${token}`,
          } : {},
        });

        if (response.ok) {
          const data = await response.json();
          setDocumentTitle(data.title || '');
          setDocumentIcon(data.icon || null);
          setDocumentProjectId(data.projectId || null);
        }
      } catch (err) {
        console.error('Error fetching document title:', err);
      }
    };

    fetchDocumentTitle();
  }, [token, documentId, shareToken, masterKey, isReadOnly]);

  useEffect(() => {
    const fetchProjectTitle = async (): Promise<void> => {
      const projectIdToFetch = projectId || documentProjectId;
      if (!token || !projectIdToFetch || projectIdToFetch === 'new') {
        setProjectTitle('');
        setProjectMainDocumentIcon(null);
        setProjectMainDocumentId(null);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/projects/${projectIdToFetch}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setProjectTitle(data.title || '');
          setProjectMainDocumentIcon(data.mainDocumentIcon || null);
          setProjectMainDocumentId(data.mainDocumentId || null);
        } else {
          setProjectTitle('');
          setProjectMainDocumentIcon(null);
          setProjectMainDocumentId(null);
        }
      } catch (err) {
        console.error('Error fetching project title:', err);
        setProjectTitle('');
        setProjectMainDocumentIcon(null);
        setProjectMainDocumentId(null);
      }
    };

    fetchProjectTitle();
  }, [token, projectId, documentProjectId]);

  useEffect(() => {
    const fetchProjectDocuments = async (): Promise<void> => {
      const projectIdToFetch = projectId || documentProjectId;
      if (!token || !projectIdToFetch || projectIdToFetch === 'new') {
        setProjectDocuments([]);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/projects/${projectIdToFetch}/documents`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const docs = Array.isArray(data.documents) ? data.documents : [];
          setProjectDocuments(
            docs.map((doc: { id: string; title?: string; icon?: string | null; pinned?: boolean }) => ({
              id: doc.id,
              title: doc.title || '',
              icon: doc.icon ?? null,
              pinned: doc.pinned === true,
            }))
          );
        } else {
          setProjectDocuments([]);
        }
      } catch (err) {
        console.error('Error fetching project documents:', err);
        setProjectDocuments([]);
      }
    };

    void fetchProjectDocuments();

    const handleDocumentCreated = (event: Event): void => {
      const detail = (event as CustomEvent<{ projectId?: string }>).detail;
      const projectIdToFetch = projectId || documentProjectId;
      if (!detail?.projectId || !projectIdToFetch || detail.projectId !== projectIdToFetch) {
        return;
      }
      void fetchProjectDocuments();
    };

    window.addEventListener('document-created', handleDocumentCreated);
    return () => window.removeEventListener('document-created', handleDocumentCreated);
  }, [token, projectId, documentProjectId, documentId]);

  useEffect(() => {
    const handleDocumentTitleUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<{ documentId: string; title: string }>;
      setProjectDocuments((prev) =>
        prev.map((doc) =>
          doc.id === customEvent.detail.documentId
            ? { ...doc, title: customEvent.detail.title }
            : doc
        )
      );
    };

    const handleDocumentIconUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<{ documentId: string; icon: string | null }>;
      setProjectDocuments((prev) =>
        prev.map((doc) =>
          doc.id === customEvent.detail.documentId
            ? { ...doc, icon: customEvent.detail.icon }
            : doc
        )
      );
    };

    const handleDocumentDeleted = (event: Event): void => {
      const customEvent = event as CustomEvent<{ documentId: string }>;
      setProjectDocuments((prev) => prev.filter((doc) => doc.id !== customEvent.detail.documentId));
    };

    const handleDocumentArchived = (event: Event): void => {
      const customEvent = event as CustomEvent<{ documentId: string }>;
      setProjectDocuments((prev) => prev.filter((doc) => doc.id !== customEvent.detail.documentId));
    };

    const handleDocumentPinnedUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<{ documentId: string; pinned: boolean }>;
      setProjectDocuments((prev) =>
        prev.map((doc) =>
          doc.id === customEvent.detail.documentId
            ? { ...doc, pinned: customEvent.detail.pinned }
            : doc
        )
      );
    };

    window.addEventListener('document-title-updated', handleDocumentTitleUpdate);
    window.addEventListener('document-icon-updated', handleDocumentIconUpdate);
    window.addEventListener('document-deleted', handleDocumentDeleted);
    window.addEventListener('document-archived', handleDocumentArchived);
    window.addEventListener('document-pinned-updated', handleDocumentPinnedUpdate);

    return () => {
      window.removeEventListener('document-title-updated', handleDocumentTitleUpdate);
      window.removeEventListener('document-icon-updated', handleDocumentIconUpdate);
      window.removeEventListener('document-deleted', handleDocumentDeleted);
      window.removeEventListener('document-archived', handleDocumentArchived);
      window.removeEventListener('document-pinned-updated', handleDocumentPinnedUpdate);
    };
  }, []);

  const handleSelectDocument = useCallback(
    (nextDocumentId: string): void => {
      if (!nextDocumentId || nextDocumentId === documentId) {
        return;
      }
      const projectIdForPath = projectId || documentProjectId || undefined;
      if (mainAreaView) {
        navigate(getDocumentPanelPath(projectIdForPath, nextDocumentId, mainAreaView));
      } else {
        navigate(getDocumentPath(projectIdForPath, nextDocumentId));
      }
    },
    [documentId, projectId, documentProjectId, mainAreaView, navigate]
  );

  const handleCreateDocument = useCallback(async (): Promise<void> => {
    const projectIdForCreate = projectId || documentProjectId;
    if (!token || !projectIdForCreate || projectIdForCreate === 'new' || isReadOnly) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectIdForCreate}/documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: t('untitled_document'),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.errorCode === 'DOCUMENT_LIMIT_REACHED') {
          showErrorToast(t('document_limit_reached') || 'Document limit reached');
          return;
        }
        throw new Error('Failed to create document');
      }

      const data = await response.json();
      setProjectDocuments((prev) => [
        {
          id: data.id,
          title: data.title || t('untitled_document'),
          icon: null,
          pinned: false,
        },
        ...prev.filter((doc) => doc.id !== data.id),
      ]);
      window.dispatchEvent(
        new CustomEvent('document-created', {
          detail: {
            documentId: data.id,
            projectId: projectIdForCreate,
            title: data.title || t('untitled_document'),
          },
        })
      );
      navigate(getDocumentPath(projectIdForCreate, data.id));
    } catch (error) {
      console.error('Error creating document:', error);
      showErrorToast(t('failed_to_create_document'));
    }
  }, [token, projectId, documentProjectId, isReadOnly, t, navigate, showErrorToast]);

  useEffect(() => {
    if (projectMainDocumentIcon) {
      updateFavicon(projectMainDocumentIcon);
    } else {
      updateFavicon(null);
    }
  }, [projectMainDocumentIcon]);

  const renderMainAreaContent = (): React.ReactNode => {
    if (!mainAreaView) {
      return null;
    }
    switch (mainAreaView) {
      case 'technicalSheet':
        if (!documentId) {
          return null;
        }
        return (
          <TechnicalSheet
            isVisible={true}
            token={token}
            documentId={documentId}
            t={t}
            showErrorToast={showErrorToast}
            isSidebar={false}
            hideHeading={true}
            onClose={handleCloseMainArea}
          />
        );
      case 'export':
        return (
          <ExportDocument
            editor={editor}
            ydoc={ydoc}
            documentTitle={documentTitle}
            projectTitle={projectTitle}
            documentId={documentId ?? undefined}
            token={token}
            comments={comments}
            suggestions={suggestions}
            t={t}
            hideHeading={true}
            onClose={handleCloseMainArea}
          />
        );
      case 'share':
        return (
          <ShareDocument
            token={token}
            documentId={documentId ?? undefined}
            projectId={projectId}
            documentProjectId={documentProjectId}
            t={t}
            showToast={showToast}
            hideHeading={true}
            onClose={handleCloseMainArea}
          />
        );
      case 'versionHistory':
        if (!documentId) {
          return null;
        }
        return (
          <VersionHistory
            documentId={documentId}
            onVersionRestored={() => {
              setTimeout(() => {
                window.location.reload();
              }, 500);
            }}
            isVisible={true}
            hideHeading={true}
            onClose={handleCloseMainArea}
          />
        );
      case 'stats':
        if (!documentId) {
          return null;
        }
        return (
          <DocumentStats
            documentId={documentId}
            wordCount={wordCount}
            readingTime={readingTime}
            pageCount={pageCount}
            selectedWordCount={selectedTextWordCount}
            selectedReadingTime={selectedTextReadingTime}
            selectedPageCount={selectedTextPageCount}
            t={t}
            hideHeading={true}
            onClose={handleCloseMainArea}
          />
        );
      case 'settings':
        if (!documentId) {
          return null;
        }
        return (
          <WritingSettings
            documentId={documentId}
            projectId={projectId}
            onUpdate={() => {
              loadSuggestions();
            }}
            hideHeading={true}
            onClose={handleCloseMainArea}
          />
        );
      case 'suggestions': {
        const pendingSuggestions = sortPendingSuggestionsByDocumentOrder(editor, suggestions);
        const analyzeDisableReason = getFeedbackAnalyzeDisableReason(editor, {
          allowDocumentFallback: true,
        });
        const isAnalyzeDisabled = analyzeDisableReason !== null;
        const analyzeTooltip = getFeedbackAnalyzeDisableTooltip(analyzeDisableReason, t);
        const getIssueTypeLabel = (issueType: string): string =>
          (issueTypeLabels as Record<string, string>)[issueType] ?? issueType;
        const issueTypeLabels: Record<string, string> = {
          orthographic: t('orthographic'),
          preciseness: t('preciseness'),
          clarity: t('clarity'),
          consistency: t('consistency'),
          orthographic_consistency: t('orthographic_consistency'),
          grammar: t('grammar'),
          spelling: t('spelling'),
          punctuation: t('punctuation'),
          sentence_structure: t('sentence_structure'),
          repetition: t('repetition'),
          overused_words: t('overused_words'),
          cacophony: t('cacophony'),
          passive_voice: t('passive_voice'),
          show_vs_tell: t('show_vs_tell'),
          pacing: t('pacing'),
          adverbs: t('adverbs'),
          adjectives: t('adjectives'),
          cliches: t('cliches'),
          transitions: t('transitions'),
          style: t('style'),
          strength: t('strength'),
          user_edit: t('suggestion_issue_type_user_edit'),
        };
        return (
          <div className="flex flex-col h-full min-w-0">
            <div className="flex flex-col gap-3 pb-4 flex-shrink-0 min-w-0">
              <SuggestionsPanelHeaderActions
                pendingSuggestionsCount={pendingSuggestions.length}
                onDismissAll={handleDismissAllSuggestions}
                isDismissAllDisabled={pendingSuggestions.some((suggestion) => isTemporarySuggestionId(suggestion.id))}
                dismissAllDisabledTooltip={t('dismiss_all_suggestions_unavailable_until_saved')}
                onTriggerAnalysis={triggerNewReview}
                isAnalyzing={isLoadingFeedback}
                isAnalyzeDisabled={isAnalyzeDisabled}
                analyzeTooltip={analyzeTooltip}
                t={t}
              />
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              {pendingSuggestions.length === 0 && !isLoadingFeedback ? (
                <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">{t('no_suggestions')}</div>
              ) : (
                <div className="flex flex-col">
                  {pendingSuggestions.map((suggestion, index) => (
                    <SuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onScrollToSuggestion={scrollToSuggestionInDocument}
                      onApplySuggestion={handleApplySuggestion}
                      onRejectSuggestion={handleRejectSuggestion}
                      onDismissSuggestion={handleDismissSuggestion}
                      t={t}
                      getIssueTypeLabel={getIssueTypeLabel}
                      showDivider={index < pendingSuggestions.length - 1}
                      showOriginalAtTop={true}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      }
      case 'comments': {
        if (!documentId) return null;
        return (
          <div className="flex flex-col h-full min-h-0 min-w-0">
            <Tabs value={commentsMainPanelTab} onValueChange={(v) => setCommentsMainPanelTab(v as 'pending' | 'deleted')} className="flex flex-col flex-1 min-h-0">
              <div className="flex flex-col gap-3 mb-4 flex-shrink-0 min-w-0">
                <TabsList className="h-7 w-fit">
                  <TabsTrigger value="pending" className="text-xs px-2 py-1 h-6">{t('pending')}</TabsTrigger>
                  <TabsTrigger value="deleted" className="text-xs px-2 py-1 h-6">{t('comments_resolved_tab')}</TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="pending" className="flex-1 overflow-y-auto min-h-0 mt-0">
                <Comments
                  editor={editor}
                  documentId={documentId}
                  isReadOnly={isReadOnly}
                  isVisible={mainAreaView === 'comments' && commentsMainPanelTab === 'pending'}
                  refreshTrigger={commentsRefreshTrigger}
                  isFocusMode={isFocusMode}
                  comments={comments}
                  onLoadComments={async () => {
                    await syncCommentLists();
                    setCommentsRefreshTrigger((prev) => prev + 1);
                  }}
                  documentOwnerId={documentOwnerId}
                  hideHeading={true}
                />
              </TabsContent>
              <TabsContent value="deleted" className="flex-1 overflow-y-auto min-h-0 mt-0">
                <Comments
                  editor={editor}
                  documentId={documentId}
                  isReadOnly={isReadOnly}
                  isVisible={mainAreaView === 'comments' && commentsMainPanelTab === 'deleted'}
                  refreshTrigger={commentsRefreshTrigger}
                  isFocusMode={isFocusMode}
                  comments={deletedComments}
                  onLoadComments={async () => {
                    await syncCommentLists();
                    setCommentsRefreshTrigger((prev) => prev + 1);
                  }}
                  documentOwnerId={documentOwnerId}
                  hideHeading={true}
                  commentThreadList="resolved"
                />
              </TabsContent>
            </Tabs>
          </div>
        );
      }
      case 'chat': {
        if (!editor) return null;
        return (
          <div className="flex flex-col h-full">
            <div className="flex-1 min-h-0">
              <MainAreaDocumentChat
                editor={editor}
                documentId={documentId ?? null}
                shareToken={shareToken ?? null}
              />
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  const updateDocumentTitle = async (title: string): Promise<boolean> => {
    if (!token || !documentId || documentId === 'new' || !title.trim()) {
      return false;
    }

    try {
      const response = await offlineFetch(`${API_BASE}/api/documents/${documentId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title: title.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to update document title');
      }

      window.dispatchEvent(new CustomEvent('document-title-updated', {
        detail: { documentId, title: title.trim() },
      }));

      const projectIdToRefresh = projectId || documentProjectId;
      if (projectIdToRefresh && projectIdToRefresh !== 'new') {
        const projectRes = await fetch(`${API_BASE}/api/projects/${projectIdToRefresh}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (projectRes.ok) {
          const projectData = await projectRes.json();
          const refreshedTitle = projectData.title || '';
          setProjectTitle(refreshedTitle);
          setProjectMainDocumentIcon(projectData.mainDocumentIcon || null);
          window.dispatchEvent(new CustomEvent('project-title-updated', {
            detail: { projectId: projectIdToRefresh, title: refreshedTitle },
          }));
        }
      }
      return true;
    } catch (err) {
      if (err instanceof Error && err.message === 'Request queued for offline processing') {
        showToast(t('title_will_be_saved_when_online') || 'Title will be saved when online');
        return false;
      }
      console.error('Error updating document title:', err);
      return false;
    }
  };

  const updateProjectTitle = async (title: string): Promise<boolean> => {
    const projectIdToUpdate = projectId || documentProjectId;
    if (!token || !projectIdToUpdate || projectIdToUpdate === 'new' || !title.trim()) {
      return false;
    }

    try {
      const response = await offlineFetch(`${API_BASE}/api/projects/${projectIdToUpdate}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title: title.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to update project title');
      }

      window.dispatchEvent(new CustomEvent('project-title-updated', {
        detail: { projectId: projectIdToUpdate, title: title.trim() },
      }));
      return true;
    } catch (err) {
      if (err instanceof Error && err.message === 'Request queued for offline processing') {
        showToast(t('title_will_be_saved_when_online') || 'Title will be saved when online');
        return false;
      }
      console.error('Error updating project title:', err);
      return false;
    }
  };

  const handleProjectTitleChange = async (value: string): Promise<boolean> => {
    setProjectTitle(value);
    return updateProjectTitle(value);
  };

  const handleTitleChange = async (value: string): Promise<boolean> => {
    setDocumentTitle(value);
    return updateDocumentTitle(value);
  };

  const handleIconChange = async (icon: string | null): Promise<void> => {
    if (!documentId || documentId === 'new' || isReadOnly) {
      return;
    }

    setDocumentIcon(icon);

    try {
      const response = await offlineFetch(`${API_BASE}/api/documents/${documentId}/icon`, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
          ...(masterKey ? { 'X-Master-Key': masterKey } : {}),
        },
        body: JSON.stringify({ icon }),
      });

      if (!response.ok) {
        console.error('Failed to update document icon');
      } else {
        window.dispatchEvent(new CustomEvent('document-icon-updated', {
          detail: { documentId, icon },
        }));
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'Request queued for offline processing') {
        showToast(t('icon_will_be_saved_when_online') || 'Icon will be saved when online');
      } else {
        console.error('Error updating document icon:', err);
      }
    }
  };

  useEffect(() => {
    const handleOnline = () => {
      offlineQueue.processQueue();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  useEffect(() => {
    if (connectionError && connectionError !== previousConnectionErrorRef.current) {
      showErrorToast(connectionError);
      previousConnectionErrorRef.current = connectionError;
      return;
    }

    if (!connectionError && previousConnectionErrorRef.current) {
      const wasConnectionError = toast === previousConnectionErrorRef.current && isErrorToast;
      previousConnectionErrorRef.current = null;
      if (wasConnectionError) {
        hideToast();
      }
    }
  }, [connectionError, hideToast, isErrorToast, showErrorToast, toast]);


  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (mainAreaView !== null) {
          return;
        }
        if (isCommentModalOpen || isSynonymsPopupOpen) {
          return;
        }
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        if (editor && !editor.state.selection.empty) {
          return;
        }
        e.preventDefault();
        setIsFocusMode(!isFocusMode);
      }
      const openSearch = (replaceMode: boolean): void => {
        if (!editor) {
          return;
        }
        const target = e.target as HTMLElement;
        const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
        const isEditorFocused = editor.isFocused || (editor.view.dom.contains(target) && !isInput);
        if (!isEditorFocused) {
          return;
        }
        e.preventDefault();
        const { selection } = editor.state;
        const selectedText = selection.empty
          ? ''
          : editor.state.doc.textBetween(selection.from, selection.to, '\n');
        if (selectedText) {
          setSearchInitialText(selectedText);
        } else if (!isSearchVisibleRef.current) {
          setSearchInitialText('');
        }
        setIsSearchVisible(true);
        setIsReplaceMode(replaceMode);
      };

      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        openSearch(false);
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'R') {
        openSearch(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor, isFocusMode, isCommentModalOpen, isSynonymsPopupOpen, mainAreaView, setIsFocusMode]);

  const extractHeadingsRef = useRef<(() => void) | null>(null);
  const previousTocDataRef = useRef<string>('');

  useEffect(() => {
    if (!editor) return;

    const extractHeadingsOnly = (): void => {
      setTocHeadings(extractTocEntries(editor.state.doc, { includeParagraphPreviews: false }).headings);
    };

    const extractHeadingsAndParagraphs = (): void => {
      const { headings, paragraphs } = extractTocEntries(editor.state.doc);
      const tocDataString = JSON.stringify({ headings, paragraphs });
      if (tocDataString === previousTocDataRef.current) {
        return;
      }
      previousTocDataRef.current = tocDataString;

      setTocHeadings(headings);
      setTocParagraphs(paragraphs);
    };

    extractHeadingsRef.current = extractHeadingsAndParagraphs;

    extractHeadingsOnly();
    editor.on('create', extractHeadingsOnly as any);

    return () => {
      editor.off('create', extractHeadingsOnly as any);
      extractHeadingsRef.current = null;
    };
  }, [editor]);

  useEffect(() => {
    if (!editor || !isDocumentLoaded) return;

    const extractHeadingsAndParagraphs = (): void => {
      const { headings, paragraphs } = extractTocEntries(editor.state.doc);
      const tocDataString = JSON.stringify({ headings, paragraphs });
      if (tocDataString === previousTocDataRef.current) {
        return;
      }
      previousTocDataRef.current = tocDataString;

      setTocHeadings(headings);
      setTocParagraphs(paragraphs);
    };

    const extractHeadingsWithoutParagraphPreviews = (): void => {
      const { headings } = extractTocEntries(editor.state.doc, { includeParagraphPreviews: false });
      const tocDataString = JSON.stringify({ headings });
      if (tocDataString === previousTocDataRef.current) {
        return;
      }
      previousTocDataRef.current = tocDataString;
      setTocHeadings(headings);
    };

    if (isFloatingTocVisible || isTocPinned) {
      extractHeadingsAndParagraphs();
      const tocUpdateTimeoutRef: { current: ReturnType<typeof setTimeout> | null } = { current: null };
      const scheduleExtractHeadingsAndParagraphs = (): void => {
        scheduleTrailingTimeout(tocUpdateTimeoutRef, extractHeadingsAndParagraphs, EDITOR_IDLE_WORK_DEBOUNCE_MS);
      };
      editor.on('update', scheduleExtractHeadingsAndParagraphs);
      return () => {
        if (tocUpdateTimeoutRef.current !== null) {
          clearTimeout(tocUpdateTimeoutRef.current);
        }
        editor.off('update', scheduleExtractHeadingsAndParagraphs);
      };
    }

    extractHeadingsWithoutParagraphPreviews();
    return undefined;
  }, [editor, isDocumentLoaded, isFloatingTocVisible, isTocPinned]);

  useEffect(() => {
    if (!editor || (!isFloatingTocVisible && !isTocPinned)) {
      return undefined;
    }

    const getTocItems = () => (tocHeadings.length > 0 ? tocHeadings : tocParagraphs);

    const updateActiveTocPosFromScroll = (): void => {
      if (ignoreTocScrollActiveUpdatesRef.current) {
        return;
      }
      const scrollContainer = editorScrollContainerRef.current;
      const viewportTop = (scrollContainer?.getBoundingClientRect().top ?? 0) + TOC_ACTIVE_VIEWPORT_OFFSET_PX;
      setActiveTocPos(
        findActiveTocItemPos(
          getTocItems(),
          (pos) => {
            try {
              return editor.view.coordsAtPos(Math.min(pos + 1, editor.state.doc.content.size)).top;
            } catch {
              return null;
            }
          },
          viewportTop,
          TOC_ACTIVE_VIEWPORT_SLOP_PX,
        ),
      );
    };

    const finishProgrammaticTocScroll = (): void => {
      ignoreTocScrollActiveUpdatesRef.current = false;
      updateActiveTocPosFromScroll();
    };

    const updateActiveTocPosFromSelection = (): void => {
      setActiveTocPos(findActiveTocItemPosAtDocPos(getTocItems(), editor.state.selection.from));
    };

    updateActiveTocPosFromScroll();
    const scrollContainer = editorScrollContainerRef.current;
    scrollContainer?.addEventListener('scroll', updateActiveTocPosFromScroll, { passive: true });
    scrollContainer?.addEventListener('scrollend', finishProgrammaticTocScroll);
    editor.on('selectionUpdate', updateActiveTocPosFromSelection);

    return () => {
      scrollContainer?.removeEventListener('scroll', updateActiveTocPosFromScroll);
      scrollContainer?.removeEventListener('scrollend', finishProgrammaticTocScroll);
      editor.off('selectionUpdate', updateActiveTocPosFromSelection);
    };
  }, [editor, isFloatingTocVisible, isTocPinned, tocHeadings, tocParagraphs]);


  const fetchFeedbackForText = async (text: string, selectionRange: { from: number; to: number } | null, targetDocumentId: string | undefined): Promise<void> => {
    if (!editor || !token || isReadOnly) {
      setIsLoadingFeedback(false);
      return;
    }

    if (!masterKey) {
      const hasCredits = await checkCredits();
      if (!hasCredits) {
        setIsLoadingFeedback(false);
        return;
      }
    }

    setIsLoadingFeedback(true);
    setFeedback('');

    const abortController = new AbortController();
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      timeoutId = setTimeout(() => {
        if (!abortController.signal.aborted) {
          abortController.abort();
        }
      }, 120000);

      const effectiveDocumentId = targetDocumentId && targetDocumentId !== 'new' ? targetDocumentId : undefined;
      const response = await fetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          text,
          documentId: effectiveDocumentId,
          forceNew: true,
          ...(selectionRange
            ? { selectionFromPos: selectionRange.from, selectionToPos: selectionRange.to }
            : {}),
        }),
        signal: abortController.signal,
      });

      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (!response.ok) {
        let errorMessage = 'Failed to fetch feedback';
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          errorMessage = `Failed to fetch feedback: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const highlightIssue = (issue: {
        text: string;
        type: string;
        feedback: string;
        suggestion?: string;
      }, index: number): void => {
      };

      let hasError = false;
      while (true) {
        if (abortController.signal.aborted) {
          reader.cancel();
          throw new DOMException('The operation was aborted.', 'AbortError');
        }
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            const update = JSON.parse(line);

            if (update.type === 'error' && update.value) {
              console.error('[Feedback Error]', update.value);
              showErrorToast(update.value);
              setIsLoadingFeedback(false);
              hasError = true;
              break;
            }

            if (update.type === 'issue' && update.value) {
              const issue = update.value;
              setFeedbackData((prev) => {
                const existingIssues = prev?.issues || [];
                const newIssues = [...existingIssues, issue];
                const issueIndex = newIssues.length - 1;
                highlightIssue(issue, issueIndex);
                return {
                  issues: newIssues
                };
              });

              const tempSuggestion = {
                id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                userId: user?.id || '',
                documentId: documentId || null,
                originalText: issue.text,
                suggestionText: issue.suggestion || '',
                issueType: issue.type as 'orthographic' | 'preciseness' | 'clarity' | 'style' | 'consistency' | 'repetition' | 'cacophony' | 'cliches' | 'adverbs' | 'adjectives' | 'strength',
                feedbackText: issue.feedback,
                llmCallId: null,
                languageCode: null,
                languageName: null,
                shownAt: new Date().toISOString(),
                status: 'pending' as const,
                closedAt: null,
                createdAt: new Date().toISOString(),
              };

              setSuggestions((prev) => {
                const existing = prev.filter(
                  (s) => (s.originalText !== issue.text || s.issueType !== issue.type) && s.status !== 'applied'
                );
                return [...existing, tempSuggestion];
              });
            }
          } catch (parseErr) {
            console.error('Error parsing update line:', parseErr, line);
          }
        }

        if (hasError) {
          break;
        }
      }

      if (buffer.trim()) {
        try {
          const update = JSON.parse(buffer.trim());
          if (update.type === 'error' && update.value) {
            console.error('[Feedback Error in buffer]', update.value);
            showErrorToast(update.value);
            setIsLoadingFeedback(false);
              } else if (update.type === 'issue' && update.value) {
                const issue = update.value;
                setFeedbackData((prev) => {
                  const existingIssues = prev?.issues || [];
                  const newIssues = [...existingIssues, issue];
                  const issueIndex = newIssues.length - 1;
                  highlightIssue(issue, issueIndex);
                  return {
                    issues: newIssues
                  };
                });

                const tempSuggestion = {
                  id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    userId: user?.id || '',
                  documentId: documentId || null,
                  originalText: issue.text,
                  suggestionText: issue.suggestion || '',
                  issueType: issue.type as 'orthographic' | 'preciseness' | 'clarity' | 'style' | 'consistency' | 'repetition' | 'cacophony' | 'cliches' | 'adverbs' | 'adjectives' | 'strength',
                  feedbackText: issue.feedback,
                  llmCallId: null,
                  languageCode: null,
                  languageName: null,
                  shownAt: new Date().toISOString(),
                  status: 'pending' as const,
                  closedAt: null,
                  createdAt: new Date().toISOString(),
                };

                setSuggestions((prev) => {
                  const existing = prev.filter(
                    (s) => (s.originalText !== issue.text || s.issueType !== issue.type) && s.status !== 'applied'
                  );
                  return [...existing, tempSuggestion];
                });
              }
            } catch (parseErr) {
              console.error('Error parsing final buffer:', parseErr);
            }
          }

          if (!hasError) {
            if (editor) {
              editor.chain().blur().run();
            }
            await loadSuggestions();
          }
          setIsLoadingFeedback(false);
    } catch (err) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (err instanceof Error && err.name === 'AbortError') {
        showErrorToast(t('analysis_timed_out'));
      } else {
        console.error('Error fetching feedback:', err);
        const errorMessage = err instanceof Error ? err.message : 'Error loading feedback. Please try again.';
        showErrorToast(errorMessage);
      }
      setIsLoadingFeedback(false);
    }
  };

  const checkDocumentHasStyleNotes = async (docId: string | undefined): Promise<boolean> => {
    if (!docId || docId === 'new' || (!token && !masterKey)) {
      return false;
    }

    try {
      const url = masterKey
        ? `${API_BASE}/api/documents/${docId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${docId}`;
      const response = await fetch(url, {
        headers: token ? {
          Authorization: `Bearer ${token}`,
        } : {},
      });

      if (response.ok) {
        const data = await response.json();
        return !!(data.styleNotes && typeof data.styleNotes === 'string' && data.styleNotes.trim().length > 0);
      }
    } catch (err) {
      console.error('Error checking document style notes:', err);
    }

    return false;
  };

  const analyzeTextRange = async (from: number, to: number): Promise<void> => {
    if (!editor || !token || isReadOnly) return;

    if (isFocusMode) {
      setIsFocusMode(false);
    }

    if (from === to) {
      return;
    }

    const selectedPlainText = getDocumentPlainText(editor.state.doc, from, to).trim();
    const selectedText = getDocumentMarkdownText(editor.state.doc, from, to).trim();
    if (!selectedPlainText || !selectedText) {
      return;
    }

    const wordCount = countWords(selectedPlainText);

    if (getFeedbackAnalyzeDisableReasonFromWordCounts(true, wordCount) !== null) {
      return;
    }

    if (wordCount > 750) {
      const hasStyleNotes = await checkDocumentHasStyleNotes(documentId);
      if (!hasStyleNotes) {
        const pendingSuggestions = suggestions.filter((s) => s.status === 'pending');
        setPendingStyleCalibrationText(selectedText);
        setPendingStyleCalibrationSelectionRange({ from, to });
        setPendingStyleCalibrationPendingCount(pendingSuggestions.length);
        setIsStyleCalibrationModalOpen(true);
        return;
      }
    }

    if (!isMobile && showSuggestionsRef.current) {
      showSuggestionsRef.current();
    }

    setIsLoadingFeedback(true);
    const currentDocumentId = documentId;
    setTimeout(() => {
      fetchFeedbackForText(selectedText, { from, to }, currentDocumentId);
    }, 300);
  };

  const analyzeTextRangeRef = useRef(analyzeTextRange);
  analyzeTextRangeRef.current = analyzeTextRange;

  const handleAnalyzeSection = useCallback((from: number, to: number): void => {
    void analyzeTextRangeRef.current(from, to);
  }, []);

  const handleMoveToNote = useCallback(async (content: string): Promise<{ projectId: string } | null> => {
    if (!token || !projectId) {
      return null;
    }
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/notes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ content }),
    });
    if (!res.ok) {
      return null;
    }
    const data = await res.json();
    return data.note ? { projectId } : null;
  }, [token, projectId]);

  const analyzeSelectedText = async (): Promise<void> => {
    if (!editor || !token || isReadOnly) return;

    const { from, to } = editor.state.selection;
    await analyzeTextRange(from, to);
  };

  const handleSynonymsClick = async (): Promise<void> => {
    if (!editor || !token || isReadOnly) return;

    if (!masterKey) {
      const hasCredits = await checkCredits();
      if (!hasCredits) {
        return;
      }
    }

    const { state, view } = editor;
    const { selection } = state;
    const { from, to } = selection;

    if (from === to) {
      return;
    }

    const selectedText = state.doc.textBetween(from, to).trim();
    if (!selectedText) {
      return;
    }

    const wordCount = selectedText.split(/\s+/).filter(w => w.length > 0).length;

    try {
      const startPos = view.coordsAtPos(from);
      const endPos = view.coordsAtPos(to);
      const selectionCenter = (startPos.left + endPos.right) / 2;
      const top = startPos.top - 50;

      setSynonymsPopupPosition({ left: selectionCenter, top: top + 60 });
      setSelectedRangeForSynonyms({ from, to });
      setSynonymsWordCount(wordCount);
      setIsSynonymsPopupOpen(true);
      setIsLoadingSynonyms(true);
      setSynonyms([]);

      const fullText = getDocumentPlainText(state.doc);
      const contextStart = Math.max(0, from - 200);
      const contextEnd = Math.min(fullText.length, to + 200);
      const context = fullText.substring(contextStart, contextEnd);

      const url = masterKey
        ? `${API_BASE}/api/synonyms?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/synonyms`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          word: selectedText,
          context,
          documentId: documentId && documentId !== 'new' ? documentId : null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch synonyms');
      }

      const data = await response.json();
      setSynonyms(data.synonyms || []);
    } catch (error) {
      console.error('Error fetching synonyms:', error);
      showErrorToast(t('failed_to_load_synonyms'));
      setIsSynonymsPopupOpen(false);
    } finally {
      setIsLoadingSynonyms(false);
    }
  };

  const handleSynonymSelect = async (synonym: string): Promise<void> => {
    if (!editor || !selectedRangeForSynonyms) return;

    const { from, to } = selectedRangeForSynonyms;
    setIsSynonymsPopupOpen(false);
    setSelectedRangeForSynonyms(null);
    editor
      .chain()
      .focus()
      .setTextSelection({ from, to })
      .insertContent(synonym)
      .setTextSelection({ from, to: from })
      .run();

    if (token && documentId && documentId !== 'new') {
      try {
        const url = masterKey
          ? `${API_BASE}/api/feature-tracking?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/feature-tracking`;
        await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            documentId,
            feature: 'synonym_selected',
          }),
        });
      } catch (trackingError) {
        console.error('Error tracking synonym selection:', trackingError);
      }
    }
  };

  const handleStyleCalibrationCancel = (): void => {
    setIsStyleCalibrationModalOpen(false);
    setPendingStyleCalibrationText(null);
    setPendingStyleCalibrationSelectionRange(null);
    setPendingStyleCalibrationPendingCount(0);
  };

  const handleStyleCalibrationAccept = (): void => {
    if (!editor || !pendingStyleCalibrationText) {
      setIsStyleCalibrationModalOpen(false);
      setPendingStyleCalibrationText(null);
      setPendingStyleCalibrationSelectionRange(null);
      return;
    }

    const startFrom = pendingStyleCalibrationSelectionRange?.from || 0;
    const selection = selectFirstNWordsExpandingToSentence(editor, 750, startFrom);

    setIsStyleCalibrationModalOpen(false);
    setPendingStyleCalibrationText(null);
    setPendingStyleCalibrationSelectionRange(null);
    setPendingStyleCalibrationPendingCount(0);

    if (selection) {
      if (!isMobile && showSuggestionsRef.current) {
        showSuggestionsRef.current();
      }

      setIsLoadingFeedback(true);
      const currentDocumentId = documentId;
      setTimeout(() => {
        fetchFeedbackForText(selection.text, { from: selection.from, to: selection.to }, currentDocumentId);
      }, 300);
    }
  };

  const triggerNewReview = async (): Promise<void> => {
    forceNewReviewRef.current = true;
    if (isFocusMode) {
      setIsFocusMode(false);
    }
    if (editor && token && !isReadOnly) {
      const { state } = editor;
      const { selection } = state;
      const { from, to } = selection;

      let text: string;
      let selectionRange: { from: number; to: number } | null = null;
      if (from !== to) {
        text = getDocumentMarkdownText(state.doc, from, to).trim();
        selectionRange = { from, to };
      } else {
        text = getDocumentMarkdownText(editor.state.doc);
      }

      if (getFeedbackAnalyzeDisableReason(editor, { allowDocumentFallback: true }) !== null) {
        return;
      }

      const wordCount = countWords(text);

      if (wordCount > 750) {
        const hasStyleNotes = await checkDocumentHasStyleNotes(documentId);
        if (!hasStyleNotes) {
          const pendingSuggestions = suggestions.filter((s) => s.status === 'pending');
          setPendingStyleCalibrationText(text);
          setPendingStyleCalibrationSelectionRange(selectionRange);
          setPendingStyleCalibrationPendingCount(pendingSuggestions.length);
          setIsStyleCalibrationModalOpen(true);
          return;
        }
      }

      if (showSuggestionsRef.current) {
        showSuggestionsRef.current();
      }

      const fetchFeedback = async (): Promise<void> => {
        setIsLoadingFeedback(true);
        setFeedback('');
        setFeedbackData(null);

        clearIssueHighlights();

        const abortController = new AbortController();
        let timeoutId: NodeJS.Timeout | null = null;

        try {
          timeoutId = setTimeout(() => {
            if (!abortController.signal.aborted) {
              abortController.abort();
            }
          }, 120000);

          const response = await fetch(`${API_BASE}/api/feedback`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ text, documentId: documentId || undefined, forceNew: true }),
            signal: abortController.signal,
          });

          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          if (!response.ok) {
            let errorMessage = 'Failed to fetch feedback';
            try {
              const errorData = await response.json();
              if (errorData.error) {
                errorMessage = errorData.error;
              }
              // Check for credit limit error
              if (response.status === 403 && (errorData.errorCode === 'CREDIT_LIMIT_REACHED' || errorMessage.includes('credit'))) {
                showCreditLimitModal();
                setIsLoadingFeedback(false);
                forceNewReviewRef.current = false;
                return;
              }
            } catch {
              errorMessage = `Failed to fetch feedback: ${response.status} ${response.statusText}`;
            }
            throw new Error(errorMessage);
          }

          if (!response.body) {
            throw new Error('No response body');
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          setFeedbackData((prev) => ({ issues: prev?.issues || [] }));

          const selectionRange = from !== to ? { from, to } : null;

          const highlightIssue = (issue: {
            text: string;
            type: 'orthographic' | 'other' | 'strength';
            feedback: string;
            suggestion?: string;
          }, index: number): void => {
          };

          let hasError = false;
          while (true) {
            if (abortController.signal.aborted) {
              reader.cancel();
              throw new DOMException('The operation was aborted.', 'AbortError');
            }
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (!line.trim()) continue;

              try {
                const update = JSON.parse(line);

                if (update.type === 'error' && update.value) {
                  const errorMessage = update.value;
                  // Check for credit limit error
                  if (errorMessage.toLowerCase().includes('credit') || errorMessage.toLowerCase().includes('limit')) {
                    showCreditLimitModal();
                  } else {
                    showErrorToast(errorMessage);
                  }
                  setIsLoadingFeedback(false);
                  forceNewReviewRef.current = false;
                  hasError = true;
                  break;
                }

                if (update.type === 'issue' && update.value) {
                  const issue = update.value;
                  setFeedbackData((prev) => {
                    const existingIssues = prev?.issues || [];
                    const newIssues = [...existingIssues, issue];
                    const issueIndex = newIssues.length - 1;
                    highlightIssue(issue, issueIndex);
                    return {
                      issues: newIssues
                    };
                  });

                  const tempSuggestion = {
                    id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    userId: user?.id || '',
                    documentId: documentId || null,
                    originalText: issue.text,
                    suggestionText: issue.suggestion || '',
                    issueType: issue.type as 'orthographic' | 'preciseness' | 'clarity' | 'style' | 'consistency' | 'repetition' | 'cacophony' | 'cliches' | 'adverbs' | 'adjectives' | 'strength',
                    feedbackText: issue.feedback,
                    llmCallId: null,
                    languageCode: null,
                    languageName: null,
                    shownAt: new Date().toISOString(),
                    status: 'pending' as const,
                    closedAt: null,
                    createdAt: new Date().toISOString(),
                  };

                  setSuggestions((prev) => {
                    const existing = prev.filter((s) => s.originalText !== issue.text || s.issueType !== issue.type);
                    return [...existing, tempSuggestion];
                  });
                }
              } catch (parseErr) {
                console.error('Error parsing update line:', parseErr, line);
              }
            }
          }

          if (buffer.trim()) {
            try {
              const update = JSON.parse(buffer.trim());
              if (update.type === 'error' && update.value) {
                const errorMessage = update.value;
                // Check for credit limit error
                if (errorMessage.toLowerCase().includes('credit') || errorMessage.toLowerCase().includes('limit')) {
                  showCreditLimitModal();
                } else {
                  showErrorToast(errorMessage);
                }
                setIsLoadingFeedback(false);
                forceNewReviewRef.current = false;
              } else if (update.type === 'issue' && update.value) {
                const issue = update.value;
                setFeedbackData((prev) => {
                  const existingIssues = prev?.issues || [];
                  const newIssues = [...existingIssues, issue];
                  const issueIndex = newIssues.length - 1;
                  highlightIssue(issue, issueIndex);
                  return {
                    issues: newIssues
                  };
                });

                const tempSuggestion = {
                  id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  userId: user?.id || '',
                  documentId: documentId || null,
                  originalText: issue.text,
                  suggestionText: issue.suggestion || '',
                  issueType: issue.type as 'orthographic' | 'preciseness' | 'clarity' | 'style' | 'consistency' | 'repetition' | 'cacophony' | 'cliches' | 'adverbs' | 'adjectives' | 'strength',
                  feedbackText: issue.feedback,
                  llmCallId: null,
                  languageCode: null,
                  languageName: null,
                  shownAt: new Date().toISOString(),
                  status: 'pending' as const,
                  closedAt: null,
                  createdAt: new Date().toISOString(),
                };

                setSuggestions((prev) => {
                  const existing = prev.filter((s) => s.originalText !== issue.text || s.issueType !== issue.type);
                  return [...existing, tempSuggestion];
                });
              }
            } catch (parseErr) {
              console.error('Error parsing final buffer:', parseErr);
            }
          }

          if (!hasError) {
            if (editor) {
              editor.chain().blur().run();
            }
            await loadSuggestions();
          }
          setIsLoadingFeedback(false);
          forceNewReviewRef.current = false;
        } catch (err) {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          if (err instanceof Error && err.name === 'AbortError') {
            showErrorToast(t('analysis_timed_out'));
          } else {
            console.error('Error fetching feedback:', err);
            const errorMessage = err instanceof Error ? err.message : 'Error loading feedback. Please try again.';
            showErrorToast(errorMessage);
          }
          setIsLoadingFeedback(false);
          forceNewReviewRef.current = false;
        }
      };

      fetchFeedback();
    }
  };


  const clearSuggestionHighlights = (): void => {
    if (!editor) return;
    const { state } = editor;
    const markType = state.schema.marks.highlightColor;
    if (!markType) return;

    const tr = state.tr;
    let hasChanges = false;

    state.doc.descendants((node: ProseMirrorNode, pos: number) => {
      if (node.isInline && node.marks) {
        node.marks.forEach((mark: Mark) => {
          if (mark.type.name === 'highlightColor' && mark.attrs.suggestionId) {
            tr.removeMark(pos, pos + (node.textContent?.length || node.nodeSize), markType);
            hasChanges = true;
          }
        });
      }
    });

    if (hasChanges) {
      editor.view.dispatch(tr);
    }
    syncSuggestionInsertMarkers(editor, []);
  };

  const clearCommentHighlights = (): void => {
    if (!editor) return;
    const { state } = editor;
    const markType = state.schema.marks.highlightColor;
    if (!markType) return;

    const tr = state.tr;
    let hasChanges = false;

    const rangesToClear: Array<{ from: number; to: number }> = [];

    state.doc.descendants((node: ProseMirrorNode, pos: number) => {
      if (node.isInline && node.marks) {
        node.marks.forEach((mark: Mark) => {
          if (mark.type.name === 'highlightColor' && mark.attrs.commentId) {
            rangesToClear.push({ from: pos, to: pos + (node.textContent?.length || node.nodeSize) });
            hasChanges = true;
          }
        });
      }
    });

    rangesToClear.forEach(({ from, to }) => {
      tr.removeMark(from, to, markType);
    });

    if (hasChanges) {
      editor.view.dispatch(tr);
    }
  };

  const clearSuggestionHighlight = (suggestionId: string): void => {
    if (!editor) return;
    const { state } = editor;
    const markType = state.schema.marks.highlightColor;
    if (!markType) return;

    const tr = state.tr;
    let hasChanges = false;

    state.doc.descendants((node: ProseMirrorNode, pos: number) => {
      if (node.isInline && node.marks) {
        node.marks.forEach((mark: Mark) => {
          if (mark.type.name === 'highlightColor' && 
              mark.attrs.suggestionId === suggestionId) {
            tr.removeMark(pos, pos + (node.textContent?.length || node.nodeSize), markType);
            hasChanges = true;
          }
        });
      }
    });

    if (hasChanges) {
      editor.view.dispatch(tr);
    }

    syncSuggestionInsertMarkers(
      editor,
      suggestions.filter((suggestion) => suggestion.id !== suggestionId),
    );
  };

  const handleApplySuggestion = async (suggestion: typeof suggestions[0]): Promise<void> => {
    if (!token || !editor) return;
    if (isTemporarySuggestionId(suggestion.id)) return;
    clearSuggestionHighlight(suggestion.id);
    
    if (suggestion.issueType !== 'strength') {
      const pendingSuggestions = sortPendingSuggestionsByDocumentOrder(editor, suggestions);
      const position = findSuggestionTextPosition(editor, suggestion, pendingSuggestions);
      if (position) {
        if (!suggestion.originalText && suggestion.suggestionText) {
          editor.chain()
            .setTextSelection(position.from)
            .insertContent(suggestion.suggestionText)
            .focus()
            .run();
        } else if (!suggestion.suggestionText || suggestion.suggestionText === '') {
          editor.chain()
            .setTextSelection({ from: position.from, to: position.to })
            .deleteSelection()
            .setTextSelection({ from: position.from, to: position.from })
            .focus()
            .run();
        } else {
          replaceRangePreservingMarks(editor, position.from, position.to, suggestion.suggestionText);
        }
      }
    }
    
    setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
    closeSuggestionHover();
    trackSuggestionAccepted(suggestion.issueType);
    try {
      const response = await fetch(`${API_BASE}/api/suggestions/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          suggestionId: suggestion.id,
          originalText: suggestion.originalText,
          suggestionText: suggestion.suggestionText,
        }),
      });
      if (response.ok) {
        await readJsonWithOnboarding(response);
        showToast(t('suggestion_applied') || 'Suggestion applied');
      } else {
        showErrorToast(t('failed_to_apply_suggestion') || 'Failed to apply suggestion');
      }
    } catch (err) {
      console.error('Error applying suggestion:', err);
      showErrorToast(t('failed_to_apply_suggestion') || 'Failed to apply suggestion');
    }
  };

  const handleRejectSuggestion = async (suggestion: typeof suggestions[0]): Promise<void> => {
    if (!token) return;
    if (isTemporarySuggestionId(suggestion.id)) return;
    if (editor) {
      clearSuggestionHighlight(suggestion.id);
    }
    setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
    try {
      const response = await fetch(`${API_BASE}/api/suggestions/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          suggestionId: suggestion.id,
          originalText: suggestion.originalText,
          suggestionText: suggestion.suggestionText,
        }),
      });
      if (response.ok) {
        await readJsonWithOnboarding(response);
        showToast(t('suggestion_rejected') || 'Suggestion rejected');
      } else {
        showErrorToast(t('failed_to_reject_suggestion') || 'Failed to reject suggestion');
      }
    } catch (err) {
      console.error('Error rejecting suggestion:', err);
      showErrorToast(t('failed_to_reject_suggestion') || 'Failed to reject suggestion');
    }
  };

  const handleDismissSuggestion = async (suggestion: typeof suggestions[0]): Promise<void> => {
    if (!token) return;
    if (isTemporarySuggestionId(suggestion.id)) return;
    if (editor) {
      clearSuggestionHighlight(suggestion.id);
    }
    setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
    setSuggestionNotFound(null);
    closeSuggestionHover();

    try {
      const response = await fetch(`${API_BASE}/api/suggestions/dismiss`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          suggestionId: suggestion.id,
          originalText: suggestion.originalText,
          suggestionText: suggestion.suggestionText,
        }),
      });
      if (response.ok) {
        showToast(t('suggestion_dismissed') || 'Suggestion dismissed');
      } else {
        showErrorToast(t('failed_to_dismiss_suggestion') || 'Failed to dismiss suggestion');
        await loadSuggestions();
      }
    } catch (err) {
      console.error('Error dismissing suggestion:', err);
      showErrorToast(t('failed_to_dismiss_suggestion') || 'Failed to dismiss suggestion');
      await loadSuggestions();
    }
  };

  const handleDismissAllSuggestions = async (): Promise<void> => {
    if (!token || !documentId || documentId === 'new') return;
    const pending = sortPendingSuggestions(suggestions);
    if (pending.length === 0) return;
    if (pending.some((suggestion) => isTemporarySuggestionId(suggestion.id))) return;
    const persistedPending = pending.filter((suggestion) => !isTemporarySuggestionId(suggestion.id));

    if (editor) {
      clearSuggestionHighlights();
    }
    setSuggestions([]);
    setSuggestionNotFound(null);
    closeSuggestionHover();

    try {
      const response = await fetch(`${API_BASE}/api/suggestions/dismiss-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ documentId }),
      });

      if (response.ok) {
        const data: { dismissedCount?: number } = await response.json();
        if (persistedPending.length > 0 && (data.dismissedCount ?? 0) === 0) {
          showErrorToast(t('failed_to_dismiss_all_suggestions') || 'Failed to dismiss all suggestions');
          await loadSuggestions();
        } else {
          showToast(t('suggestions_all_dismissed') || 'All suggestions dismissed');
        }
      } else {
        showErrorToast(t('failed_to_dismiss_all_suggestions') || 'Failed to dismiss all suggestions');
        await loadSuggestions();
      }
    } catch (err) {
      console.error('Error dismissing all suggestions:', err);
      showErrorToast(t('failed_to_dismiss_all_suggestions') || 'Failed to dismiss all suggestions');
      await loadSuggestions();
    }
  };

  const loadComments = useCallback(async (options?: { notifyOnNew?: boolean }): Promise<void> => {
    if (!token || !documentId || documentId === 'new') {
      return;
    }
    try {
      const previousIds = new Set(commentsRef.current.map((comment) => comment.id));
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/comments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const loadedComments = data.comments || [];
        let nextComments = loadedComments;

        if (editor && loadedComments.length > 0) {
          nextComments = loadedComments.map((comment: typeof comments[0]) => {
            const recalculated = recalculateCommentPosition(editor, comment, ydoc);
            return {
              ...comment,
              fromPos: recalculated.fromPos,
              toPos: recalculated.toPos,
            };
          });
        }

        commentsRef.current = nextComments;
        setComments(nextComments);

        if (options?.notifyOnNew && !isFocusModeRef.current) {
          const newRemoteComments = nextComments.filter(
            (comment: typeof comments[0]) => (
              !previousIds.has(comment.id)
              && comment.userId !== user?.id
            ),
          );

          const revealComment = (comment: typeof comments[0]): void => {
            hideToast();
            showCommentsRef.current?.();
            window.setTimeout(() => {
              const result = scrollToCommentUtil(editor, comment, editorScrollContainerRef);
              const message = getCommentLocationLostToastMessage(result, t);
              if (message) {
                showToast(message);
              }
            }, 350);
          };

          if (newRemoteComments.length === 1) {
            const comment = newRemoteComments[0]!;
            showToast(
              <>
                {t('remote_comment_toast_before')}
                <button
                  type="button"
                  onClick={() => {
                    revealComment(comment);
                  }}
                  style={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    fontWeight: 600,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  {t('remote_comment_toast_link')}
                </button>
                {t('remote_comment_toast_after')}
              </>,
            );
          } else if (newRemoteComments.length > 1) {
            const firstComment = newRemoteComments[0]!;
            showToast(
              <>
                {t('remote_comments_toast_before')}
                <button
                  type="button"
                  onClick={() => {
                    revealComment(firstComment);
                  }}
                  style={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    fontWeight: 600,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  {t('remote_comments_toast_link').replace(
                    '{count}',
                    String(newRemoteComments.length),
                  )}
                </button>
                {t('remote_comments_toast_after')}
              </>,
            );
          }
        }
      } else {
        console.error('Failed to load comments');
      }
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  }, [token, documentId, editor, ydoc, showToast, hideToast, t, user?.id]);

  loadCommentsRef.current = loadComments;

  const loadDeletedComments = useCallback(async (): Promise<void> => {
    if (!token || !documentId || documentId === 'new') {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/comments?includeDeleted=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDeletedComments(data.comments || []);
      } else {
        console.error('Failed to load deleted comments');
      }
    } catch (err) {
      console.error('Error loading deleted comments:', err);
    }
  }, [token, documentId]);

  const syncCommentLists = useCallback(async (): Promise<void> => {
    await loadComments();
    await loadDeletedComments();
  }, [loadComments, loadDeletedComments]);

  const handleHighlightComments = useCallback((): void => {
    highlightComments(editor, comments);
  }, [editor, comments]);

  const loadSuggestions = useCallback(async (options?: { sortByDocumentOrder?: boolean; notifyOnNew?: boolean }): Promise<void> => {
    if (!token || !documentId || documentId === 'new') {
      return;
    }
    try {
      const previousIds = new Set(suggestionsRef.current.map((suggestion) => suggestion.id));
      const response = await fetch(`${API_BASE}/api/suggestions/documents/${documentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const filteredSuggestions = (data.suggestions || []).filter(
          (suggestion: typeof suggestions[0]) => suggestion.status !== 'applied'
        );
        let nextSuggestions = filteredSuggestions;
        if (options?.sortByDocumentOrder) {
          const pendingSuggestions = filteredSuggestions.filter(
            (suggestion: typeof suggestions[0]) => suggestion.status === 'pending'
          );
          const nonPendingSuggestions = filteredSuggestions.filter(
            (suggestion: typeof suggestions[0]) => suggestion.status !== 'pending'
          );
          const pendingInDocumentOrder = sortPendingSuggestionsByDocumentOrder(
            editor,
            pendingSuggestions,
          );
          nextSuggestions = [...pendingInDocumentOrder, ...nonPendingSuggestions];
        }
        suggestionsRef.current = nextSuggestions;
        setSuggestions(nextSuggestions);

        if (options?.notifyOnNew && !isFocusModeRef.current) {
          const newRemoteSuggestions = filteredSuggestions.filter(
            (suggestion: typeof suggestions[0]) => (
              !previousIds.has(suggestion.id)
              && suggestion.status === 'pending'
              && suggestion.userId !== user?.id
            )
          );
          const revealSuggestion = (suggestion: { id: string; originalText: string }): void => {
            hideToast();
            showSuggestionsRef.current?.();
            window.setTimeout(() => {
              if (!editor) {
                return;
              }
              const pendingSuggestions = sortPendingSuggestionsByDocumentOrder(
                editor,
                suggestionsRef.current,
              );
              scrollToSuggestionMark(
                editor,
                suggestion,
                editorScrollContainerRef,
                pendingSuggestions,
              );
            }, 350);
          };

          if (newRemoteSuggestions.length === 1) {
            const suggestion = newRemoteSuggestions[0]!;
            showToast(
              <>
                {t('remote_suggestion_toast_before')}
                <button
                  type="button"
                  onClick={() => {
                    revealSuggestion(suggestion);
                  }}
                  style={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    fontWeight: 600,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  {t('remote_suggestion_toast_link')}
                </button>
                {t('remote_suggestion_toast_after')}
              </>,
            );
          } else if (newRemoteSuggestions.length > 1) {
            const firstSuggestion = newRemoteSuggestions[0]!;
            showToast(
              <>
                {t('remote_suggestions_toast_before')}
                <button
                  type="button"
                  onClick={() => {
                    revealSuggestion(firstSuggestion);
                  }}
                  style={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    fontWeight: 600,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  {t('remote_suggestions_toast_link').replace(
                    '{count}',
                    String(newRemoteSuggestions.length),
                  )}
                </button>
                {t('remote_suggestions_toast_after')}
              </>,
            );
          }
        }
      } else {
        console.error('Failed to load suggestions');
      }
    } catch (err) {
      console.error('Error loading suggestions:', err);
    }
  }, [token, documentId, editor, showToast, hideToast, t, user?.id]);

  loadSuggestionsRef.current = loadSuggestions;

  const highlightSuggestions = useCallback((): void => {
    if (!editor) return;

    if (suggestions.length === 0) {
      syncSuggestionInsertMarkers(editor, []);
      return;
    }

    const { state } = editor;
    const markType = state.schema.marks.highlightColor;
    if (!markType) return;

    const tr = state.tr;
    let hasChanges = false;

    const pendingSuggestions = sortPendingSuggestionsByDocumentOrder(editor, suggestions);

    state.doc.descendants((node: ProseMirrorNode, pos: number) => {
      if (node.isInline && node.marks) {
        node.marks.forEach((mark: Mark) => {
          if (mark.type.name === 'highlightColor' && mark.attrs.suggestionId) {
            tr.removeMark(pos, pos + (node.textContent?.length || node.nodeSize), markType);
            hasChanges = true;
          }
        });
      }
    });

    const resolvedPositions: Array<{ suggestion: typeof pendingSuggestions[0]; from: number; to: number }> = [];

    pendingSuggestions.forEach((suggestion) => {
      if (isInsertionSuggestion(suggestion)) {
        return;
      }
      const position = findSuggestionTextPosition(editor, suggestion, pendingSuggestions);
      if (position && position.from < position.to) {
        resolvedPositions.push({ suggestion, from: position.from, to: position.to });
      }
    });

    resolvedPositions.forEach(({ from, to }) => {
      state.doc.nodesBetween(from, to, (node, nodeFrom) => {
        if (node.isInline && node.marks) {
          const nodeTo = nodeFrom + node.nodeSize;
          node.marks.forEach((mark: Mark) => {
            if (mark.type.name === 'highlightColor' && !mark.attrs.suggestionId && !mark.attrs.commentId && !mark.attrs.searchHighlight) {
              const markFrom = Math.max(nodeFrom, from);
              const markTo = Math.min(nodeTo, to);
              tr.removeMark(markFrom, markTo, markType);
              hasChanges = true;
            }
          });
        }
      });
    });

    resolvedPositions.forEach(({ suggestion, from, to }) => {
      const color = suggestion.issueType === 'strength'
        ? COLOR_SUCCESS_ALPHA_30
        : suggestion.issueType === 'orthographic'
        ? COLOR_DANGER_ALPHA_30
        : suggestion.issueType === 'user_edit'
        ? COLOR_SUGGESTION_USER_EDIT_ALPHA_30
        : COLOR_WARNING_ALPHA_30;
      try {
        const mark = markType.create({ color, suggestionId: suggestion.id });
        tr.addMark(from, to, mark);
        hasChanges = true;
      } catch (error) {
        console.warn('[highlightSuggestions] Error creating highlight mark for suggestion:', error, suggestion);
      }
    });

    if (hasChanges) {
      editor.view.dispatch(tr);
    }

    syncSuggestionInsertMarkers(editor, pendingSuggestions);
  }, [editor, suggestions]);

  const scrollToComment = (comment: typeof comments[0]): void => {
    scrollToCommentUtil(editor, comment, editorScrollContainerRef);
  };

  const scrollToSuggestionInDocument = (suggestion: { id: string; originalText: string }): void => {
    const pendingSuggestions = sortPendingSuggestionsByDocumentOrder(editor, suggestions);
    const fullSuggestion = suggestions.find((s) => s.id === suggestion.id);
    const found = scrollToSuggestionMark(
      editor,
      fullSuggestion ?? suggestion,
      editorScrollContainerRef,
      pendingSuggestions,
    );
    if (!found) {
      if (fullSuggestion) {
        setSuggestionNotFound(fullSuggestion);
      }
    }
  };

  const clearAllHighlights = (): void => {
    if (!editor) return;
    const { state } = editor;
    const markType = state.schema.marks.highlightColor;
    if (!markType) return;

    const tr = state.tr;
    let hasChanges = false;

    const rangesToClear: Array<{ from: number; to: number }> = [];

    state.doc.descendants((node: ProseMirrorNode, pos: number) => {
      if (node.isInline && node.marks) {
        node.marks.forEach((mark: Mark) => {
          if (mark.type.name === 'highlightColor' && mark.attrs.commentId) {
            rangesToClear.push({ from: pos, to: pos + (node.textContent?.length || node.nodeSize) });
            hasChanges = true;
          }
        });
      }
    });

    rangesToClear.forEach(({ from, to }) => {
      tr.removeMark(from, to, markType);
    });

    if (hasChanges) {
      editor.view.dispatch(tr);
    }
  };

  const clearIssueHighlights = (): void => {
    if (!editor) return;
    const { state } = editor;
    const { tr } = state;
    let hasChanges = false;

    state.doc.descendants((node: ProseMirrorNode, pos: number) => {
      if (node.isInline && node.marks) {
        node.marks.forEach((mark: Mark) => {
          if (mark.type.name === 'highlightColor' && !mark.attrs.suggestionId && !mark.attrs.commentId) {
            tr.removeMark(pos, pos + node.nodeSize, mark.type);
            hasChanges = true;
          }
        });
      }
    });

    if (hasChanges) {
      editor.view.dispatch(tr);
    }
  };

  useEffect(() => {
    if (isFocusMode) {
      clearSuggestionHighlights();
      clearCommentHighlights();
    }
  }, [isFocusMode])

  useEffect(() => {
    if (isReadOnly && editor) {
      clearAllHighlights();
    }
  }, [isReadOnly, editor]);

  useEffect(() => {
    if (isReadOnly && editor && isDocumentLoaded) {
      clearAllHighlights();
    }
  }, [isReadOnly, editor, isDocumentLoaded]);

  useEffect(() => {
    if (!editor || !isDocumentLoaded) return;
    clearSearchHighlights(editor);
  }, [editor, isDocumentLoaded]);

  useEffect(() => {
    if (!isReadOnly || !editor) return;

    if (isFocusMode) {
      clearSuggestionHighlights();
      clearCommentHighlights();
    }

    return undefined;
  }, [isReadOnly, editor, isFocusMode]);

  useEffect(() => {
    if (documentId && documentId !== 'new' && isDocumentLoaded && token && !isReadOnly) {
      loadSuggestions();
      loadComments();
    }
  }, [documentId, isDocumentLoaded, token, isReadOnly, loadSuggestions, loadComments]);

  useEffect(() => {
    const commentIdFromUrl = searchParams.get('commentId');
    if (commentIdFromUrl) {
      setPendingEditorCommentScrollId(commentIdFromUrl);
      openCommentIdHandledRef.current = null;
    }
  }, [searchParams]);

  const pendingEditorComment =
    pendingEditorCommentScrollId
      ? comments.find((entry) => entry.id === pendingEditorCommentScrollId) ??
        deletedComments.find((entry) => entry.id === pendingEditorCommentScrollId)
      : null;

  useEffect(() => {
    const commentId = pendingEditorCommentScrollId;
    if (!commentId || !editor || !isDocumentLoaded || !documentBasePath) {
      return;
    }

    if (mainAreaView === 'comments' && !isMobile) {
      const params = new URLSearchParams({ commentId, focusReply: '1' });
      navigate(`${documentBasePath}?${params.toString()}`, { replace: true });
      return;
    }

    if (mainAreaView !== null) {
      return;
    }

    if (!pendingEditorComment || pendingEditorComment.id !== commentId) {
      return;
    }

    if (
      deletedComments.some((entry) => entry.id === commentId) &&
      commentsMainPanelTab !== 'deleted'
    ) {
      setCommentsMainPanelTab('deleted');
    }

    if (!isMobile) {
      showCommentsRef.current?.();
    }

    if (openCommentIdHandledRef.current === commentId) {
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let retryTimeoutId: number | undefined;

    const tryScrollToComment = (): void => {
      if (cancelled) {
        return;
      }
      attempts += 1;
      scrollToCommentUtil(editor, pendingEditorComment, editorScrollContainerRef);
      const mark = editor.view.dom.querySelector(
        `mark[data-comment-id="${CSS.escape(commentId)}"]`
      );
      const markVisible =
        mark instanceof HTMLElement &&
        mark.getBoundingClientRect().top < window.innerHeight &&
        mark.getBoundingClientRect().bottom > 0;
      if (markVisible || attempts >= 8) {
        openCommentIdHandledRef.current = commentId;
        setPendingEditorCommentScrollId(null);
        return;
      }
      retryTimeoutId = window.setTimeout(tryScrollToComment, 250);
    };

    const timeoutId = window.setTimeout(tryScrollToComment, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (retryTimeoutId !== undefined) {
        window.clearTimeout(retryTimeoutId);
      }
    };
  }, [
    pendingEditorCommentScrollId,
    pendingEditorComment,
    deletedComments,
    commentsMainPanelTab,
    editor,
    isDocumentLoaded,
    mainAreaView,
    isMobile,
    documentBasePath,
    navigate,
  ]);

  useEffect(() => {
    if (!isFocusMode && editor) {
      const timeoutId = setTimeout(() => {
        highlightSuggestions();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isFocusMode, editor, suggestions, highlightSuggestions]);

  useEffect(() => {
    if (isFocusMode || !editor || comments.length === 0) {
      return undefined;
    }

    let cancelled = false;
    let idleId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const runHighlight = (): void => {
      if (cancelled || editor.isDestroyed) {
        return;
      }
      handleHighlightComments();
    };

    const scheduleHighlight = (): void => {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(() => {
          idleId = null;
          runHighlight();
        }, { timeout: 2000 });
        return;
      }
      timeoutId = setTimeout(runHighlight, 300);
    };

    timeoutId = setTimeout(scheduleHighlight, 0);

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      if (idleId !== null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [isFocusMode, editor, comments, handleHighlightComments]);

  useEffect(() => {
    if (mainAreaView === 'suggestions') {
      void loadSuggestions({ sortByDocumentOrder: true });
    }
  }, [mainAreaView, loadSuggestions]);

  useEffect(() => {
    if (mainAreaView === 'suggestions') {
      const timeoutId = setTimeout(() => highlightSuggestions(), 300);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [mainAreaView, highlightSuggestions, suggestions]);

  useEffect(() => {
    if (mainAreaView === 'comments' && commentsMainPanelTab === 'deleted') {
      loadDeletedComments();
    }
  }, [mainAreaView, commentsMainPanelTab, loadDeletedComments]);

  const scrollToSuggestion = (issueIndex: number): void => {
    const suggestionElement = suggestionRefs.current.get(issueIndex);
    if (suggestionElement) {
      suggestionElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const removeHighlightForIssueText = (issueText: string): void => {
    if (!editor) return;
    const position = findTextPosition(editor, stripInlineMarkdown(issueText));
    if (!position) return;

    const { state } = editor;
    const { tr } = state;
    let hasChanges = false;

    state.doc.nodesBetween(position.from, position.to, (node: ProseMirrorNode, pos: number) => {
      if (node.isInline && node.marks) {
        node.marks.forEach((mark: Mark) => {
          if (mark.type.name === 'highlightColor' && !mark.attrs.suggestionId) {
            const markFrom = Math.max(pos, position.from);
            const markTo = Math.min(pos + node.nodeSize, position.to);
            tr.removeMark(markFrom, markTo, mark.type);
            hasChanges = true;
          }
        });
      }
    });

    if (hasChanges) {
      editor.view.dispatch(tr);
    }
  };

  const applySuggestion = async (issueIndex: number): Promise<void> => {
    if (!editor || !feedbackData || issueIndex < 0 || issueIndex >= feedbackData.issues.length) return;

    const issue = feedbackData.issues[issueIndex];
    if (!issue) return;

    removeHighlightForIssueText(issue.text);

    const isPositiveFeedback = issue.type === 'strength';
    const hasSuggestion = issue.suggestion !== undefined;

    if (!isPositiveFeedback && hasSuggestion) {
      const suggestionText = issue.suggestion;
      if (suggestionText === undefined) return;
      const position = findTextPosition(editor, stripInlineMarkdown(issue.text));
      if (position) {
        if (!suggestionText || suggestionText === '') {
          editor.chain()
            .setTextSelection({ from: position.from, to: position.to })
            .deleteSelection()
            .setTextSelection({ from: position.from, to: position.from })
            .focus()
            .run();
        } else {
          replaceRangePreservingMarks(editor, position.from, position.to, suggestionText);
        }
      }
    }

    setFeedbackData((prev) => {
      if (!prev) return null;
      const newIssues = prev.issues.filter((_, idx) => idx !== issueIndex);
      return {
        issues: newIssues
      };
    });

    if (token) {
      try {
        const response = await fetch(`${API_BASE}/api/suggestions/apply`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            originalText: issue.text,
            suggestionText: issue.suggestion ?? '',
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to apply suggestion');
        }

        const data = await readJsonWithOnboarding<{ styleNotesUpdated?: boolean }>(response);

        if (data?.styleNotesUpdated) {
          showToast(t('style_notes_refinement_toast'));
        }
      } catch (err) {
        console.error('Error marking suggestion as applied:', err);
      }
    }
  };

  const rejectSuggestion = async (issueIndex: number): Promise<void> => {
    if (!feedbackData || issueIndex < 0 || issueIndex >= feedbackData.issues.length) return;

    const issue = feedbackData.issues[issueIndex];
    if (!issue) return;

    const suggestionText = issue.suggestion ?? '';

    removeHighlightForIssueText(issue.text);

    setFeedbackData((prev) => {
      if (!prev) return null;
      const newIssues = prev.issues.filter((_, idx) => idx !== issueIndex);
      return {
        issues: newIssues
      };
    });

    if (token) {
      try {
        const response = await fetch(`${API_BASE}/api/suggestions/reject`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            originalText: issue.text,
            suggestionText: suggestionText,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to reject suggestion');
        }

        const data = await readJsonWithOnboarding<{ styleNotesUpdated?: boolean }>(response);

        if (data?.styleNotesUpdated) {
          showToast(t('style_notes_refinement_toast'));
        }
      } catch (err) {
        console.error('Error marking suggestion as rejected:', err);
      }
    }
  };

  const reportSuggestion = async (issueIndex: number): Promise<void> => {
    if (!feedbackData || issueIndex < 0 || issueIndex >= feedbackData.issues.length) return;

    const issue = feedbackData.issues[issueIndex];
    if (!issue) return;

    const suggestionText = issue.suggestion ?? '';

    removeHighlightForIssueText(issue.text);

    setFeedbackData((prev) => {
      if (!prev) return null;
      const newIssues = prev.issues.filter((_, idx) => idx !== issueIndex);
      return {
        issues: newIssues
      };
    });

    if (token) {
      try {
        await fetch(`${API_BASE}/api/suggestions/report`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            originalText: issue.text,
            suggestionText: suggestionText,
          }),
        });
      } catch (err) {
        console.error('Error marking suggestion as reported:', err);
      }
    }
  };

  const dismissSuggestion = (issueIndex: number): void => {
    if (!feedbackData || issueIndex < 0 || issueIndex >= feedbackData.issues.length) return;

    const issue = feedbackData.issues[issueIndex];
    if (!issue) return;

    removeHighlightForIssueText(issue.text);

    setFeedbackData((prev) => {
      if (!prev) return null;
      const newIssues = prev.issues.filter((_, idx) => idx !== issueIndex);
      return {
        issues: newIssues
      };
    });
  };

  const scrollToHighlightedText = (issueIndex: number): void => {
    if (!editor || !feedbackData || issueIndex < 0 || issueIndex >= feedbackData.issues.length) return;

    const issue = feedbackData.issues[issueIndex];
    if (!issue) return;

    const position = findTextPosition(editor, stripInlineMarkdown(issue.text));
    if (position) {
      editor.commands.setTextSelection(position.from);
      editor.commands.focus();
      
      setTimeout(() => {
        const { view } = editor;
        const domAtPos = view.domAtPos(position.from);
        if (domAtPos.node) {
          let targetNode: Node | null = domAtPos.node;
          if (targetNode && targetNode.nodeType === Node.TEXT_NODE) {
            const textNode = targetNode as Text;
            const range = document.createRange();
            range.setStart(textNode, domAtPos.offset);
            range.setEnd(textNode, Math.min(domAtPos.offset + 1, textNode.textContent?.length || 0));
            
            const rect = range.getBoundingClientRect();
            let scrollContainer = view.dom.parentElement;
            
            while (scrollContainer && scrollContainer !== document.body) {
              const style = window.getComputedStyle(scrollContainer);
              if (style.overflow === 'auto' || style.overflowY === 'auto' || style.overflow === 'scroll' || style.overflowY === 'scroll') {
                break;
              }
              scrollContainer = scrollContainer.parentElement;
            }
            
            if (scrollContainer) {
              const container = scrollContainer as HTMLElement;
              const containerRect = container.getBoundingClientRect();
              const scrollTop = container.scrollTop + (rect.top - containerRect.top) - (container.clientHeight / 2) + (rect.height / 2);
              
              container.scrollTo({
                top: Math.max(0, scrollTop),
                behavior: 'smooth'
              });
            } else {
              view.dom.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            
            range.detach();
          } else {
            const element = targetNode as HTMLElement;
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 0);
    }
  };

  const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
    let scrollContainer = element;
    while (scrollContainer && scrollContainer !== document.body) {
      const style = window.getComputedStyle(scrollContainer);
      if (style.overflow === 'auto' || style.overflowY === 'auto' || style.overflow === 'scroll' || style.overflowY === 'scroll') {
        return scrollContainer;
      }
      scrollContainer = scrollContainer.parentElement;
    }
    return null;
  };

  const getIssueTypeLabel = (issueType: string): string => {
    const labels: Record<string, string> = {
      orthographic: t('orthographic'),
      preciseness: t('preciseness'),
      clarity: t('clarity'),
      consistency: t('consistency'),
      orthographic_consistency: t('orthographic_consistency'),
      grammar: t('grammar'),
      spelling: t('spelling'),
      punctuation: t('punctuation'),
      sentence_structure: t('sentence_structure'),
      repetition: t('repetition'),
      overused_words: t('overused_words'),
      cacophony: t('cacophony'),
      passive_voice: t('passive_voice'),
      show_vs_tell: t('show_vs_tell'),
      pacing: t('pacing'),
      adverbs: t('adverbs'),
      adjectives: t('adjectives'),
      cliches: t('cliches'),
      transitions: t('transitions'),
      style: t('style'),
      strength: t('strength'),
      user_edit: t('suggestion_issue_type_user_edit'),
    };
    return labels[issueType] || issueType;
  };

  const scrollToTocPosition = (pos: number): void => {
    if (!editor) return;

    ignoreTocScrollActiveUpdatesRef.current = true;
    setActiveTocPos(pos);
    const insidePos = pos + 1;
    editor.chain().setTextSelection(insidePos).focus().run();

    setTimeout(() => {
      const { view } = editor;
      const coords = view.coordsAtPos(insidePos);
      const scrollContainer = findScrollContainer(view.dom.parentElement);

      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const topOffset = TOC_ACTIVE_VIEWPORT_OFFSET_PX;
        const scrollTop = scrollContainer.scrollTop + (coords.top - containerRect.top) - topOffset;

        scrollContainer.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
        window.setTimeout(() => {
          if (ignoreTocScrollActiveUpdatesRef.current) {
            ignoreTocScrollActiveUpdatesRef.current = false;
          }
        }, 600);
      } else {
        ignoreTocScrollActiveUpdatesRef.current = false;
      }
    }, 0);
  };

  const moveTocSection = (fromIndex: number, toIndex: number): void => {
    if (!editor || fromIndex === toIndex || isReadOnly) return;

    const headingsWithType = tocHeadings.map((h, idx) => ({
      ...h,
      originalIndex: idx,
      type: 'heading' as const,
      kind: h.kind ?? 'heading',
    }));
    const paragraphsWithType = tocParagraphs.map((p, idx) => ({
      ...p,
      originalIndex: idx,
      type: 'paragraph' as const,
      level: 0,
      kind: 'paragraph' as const,
    }));
    const combined = [...headingsWithType, ...paragraphsWithType].sort((a, b) => a.pos - b.pos);

    const draggedItem = combined[fromIndex];
    const targetItem = combined[toIndex];

    if (!draggedItem || !targetItem) return;

    const { state, view } = editor;
    const { doc, tr } = state;

    const findSectionEnd = (startPos: number, startLevel: number): number => {
      return findSectionEndPos(doc, startPos, startLevel);
    };

    const findNodeEnd = (pos: number): number => {
      const resolvedPos = doc.resolve(pos);
      const node = resolvedPos.nodeAfter;
      if (node) {
        return pos + node.nodeSize;
      }
      return pos;
    };

    const findSceneEnd = (startPos: number): number => {
      return getSceneRange(doc, startPos)?.to ?? findNodeEnd(startPos);
    };

    let sectionStart: number;
    let sectionEnd: number;

    if (draggedItem.type === 'heading' && draggedItem.kind === 'scene') {
      sectionStart = draggedItem.pos;
      sectionEnd = findSceneEnd(sectionStart);
    } else if (draggedItem.type === 'heading') {
      const draggedHeading = tocHeadings.find(h => h.pos === draggedItem.pos);
      if (!draggedHeading) return;
      sectionStart = draggedHeading.pos;
      sectionEnd = findSectionEnd(sectionStart, draggedHeading.level);
    } else {
      sectionStart = draggedItem.pos;
      sectionEnd = findNodeEnd(sectionStart);
    }

    let targetPos: number;
    if (targetItem.type === 'heading' && targetItem.kind === 'scene') {
      if (toIndex < fromIndex) {
        targetPos = targetItem.pos;
      } else {
        targetPos = findSceneEnd(targetItem.pos);
      }
    } else if (targetItem.type === 'heading') {
      const targetHeading = tocHeadings.find(h => h.pos === targetItem.pos);
      if (!targetHeading) return;
      if (toIndex < fromIndex) {
        targetPos = targetHeading.pos;
      } else {
        targetPos = findSectionEnd(targetHeading.pos, targetHeading.level);
      }
    } else {
      if (toIndex < fromIndex) {
        targetPos = targetItem.pos;
      } else {
        targetPos = findNodeEnd(targetItem.pos);
      }
    }

    if (targetPos >= sectionStart && targetPos <= sectionEnd) return;

    const sectionSlice = doc.slice(sectionStart, sectionEnd);

    if (targetPos > sectionEnd) {
      const adjustedTargetPos = targetPos;
      const newTr = tr
        .insert(adjustedTargetPos, sectionSlice.content)
        .delete(sectionStart, sectionEnd);
      view.dispatch(newTr);
    } else {
      const newTr = tr
        .delete(sectionStart, sectionEnd)
        .insert(targetPos, sectionSlice.content);
      view.dispatch(newTr);
    }

    // Dragging happens in the TOC panel, outside the editor's contentEditable,
    // so it steals focus. Tiptap's Mod-z keymap only fires while the editor is
    // focused — without this, undo silently no-ops until some other action
    // (e.g. a click in the editor) restores focus first.
    view.focus();
  };





  const completeScrollRestore = useCallback((): void => {
    isScrollRestoreCompleteRef.current = true;
    pendingScrollRestoreRef.current = null;
  }, []);

  const restoreEditorScroll = useCallback((): void => {
    if (!editor || !documentId || isReadOnly || mainAreaView !== null) return;

    const savedState =
      pendingScrollRestoreRef.current ??
      parseReadingState(
        typeof sessionStorage !== 'undefined'
          ? sessionStorage.getItem(getReadingStateStorageKey(documentId))
          : null
      );
    if (!savedState) {
      completeScrollRestore();
      return;
    }

    const scrollContainer = editorScrollContainerRef.current;
    if (!scrollContainer) return;

    deferEditorCommand(() => {
      if (!editor || !documentId || isReadOnly || mainAreaView !== null) return;
      applyEditorScrollState(editor, scrollContainer, savedState);
      editor.commands.focus();
      completeScrollRestore();
    });
  }, [editor, documentId, isReadOnly, mainAreaView, completeScrollRestore]);

  useEffect(() => {
    if (!editor || !isDocumentLoaded || !isPositionLoaded || isReadOnly || !documentId || mainAreaView !== null) return;
    if (lastRestoredCaretDocumentIdRef.current === documentId) return;

    if (pendingEditorCommentScrollId || searchParams.get('commentId')) {
      lastRestoredCaretDocumentIdRef.current = documentId;
      completeScrollRestore();
      return;
    }

    const savedState = pendingScrollRestoreRef.current;
    if (!savedState) {
      lastRestoredCaretDocumentIdRef.current = documentId;
      completeScrollRestore();
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 60;

    const tryRestore = (): void => {
      if (cancelled) return;
      if (attempts >= maxAttempts) {
        lastRestoredCaretDocumentIdRef.current = documentId;
        completeScrollRestore();
        return;
      }
      attempts += 1;

      const scrollContainer = editorScrollContainerRef.current;
      if (!scrollContainer || editor.state.doc.content.size < 2) {
        requestAnimationFrame(tryRestore);
        return;
      }

      if (!isEditorScrollContainerReadyForRestore(scrollContainer, savedState)) {
        requestAnimationFrame(tryRestore);
        return;
      }

      applyEditorScrollState(editor, scrollContainer, savedState);
      editor.commands.focus();
      lastRestoredCaretDocumentIdRef.current = documentId;
      lastPersistedSignatureRef.current = readingStateSignature(savedState);
      completeScrollRestore();
    };

    requestAnimationFrame(tryRestore);
    return () => {
      cancelled = true;
    };
  }, [
    editor,
    isDocumentLoaded,
    isPositionLoaded,
    isReadOnly,
    documentId,
    mainAreaView,
    completeScrollRestore,
    searchParams,
    pendingEditorCommentScrollId,
  ]);

  useEffect(() => {
    if (!editor || !documentId) return undefined;

    const persistReadingState = (): void => {
      if (!isScrollRestoreCompleteRef.current) return;
      const { selection } = editor.state;
      const state: ReadingState = {
        pos: selection.from,
        scrollTop: editorScrollContainerRef.current?.scrollTop ?? 0,
      };
      const signature = readingStateSignature(state);
      if (signature === lastPersistedSignatureRef.current) return;

      lastPersistedSignatureRef.current = signature;
      writeReadingStateToSessionStorage(documentId, state);
      readingStateSaverRef.current?.schedule(state);
    };

    const persistTimeoutRef: { current: ReturnType<typeof setTimeout> | null } = { current: null };
    const schedulePersistReadingState = (): void => {
      scheduleTrailingTimeout(persistTimeoutRef, persistReadingState, EDITOR_IDLE_WORK_DEBOUNCE_MS);
    };

    if (mainAreaView !== null) return undefined;

    editor.on('selectionUpdate', schedulePersistReadingState);
    editor.on('transaction', schedulePersistReadingState);

    const scrollContainer = editorScrollContainerRef.current;
    const handleScroll = (): void => {
      schedulePersistReadingState();
    };

    scrollContainer?.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      editor.off('selectionUpdate', schedulePersistReadingState);
      editor.off('transaction', schedulePersistReadingState);
      if (persistTimeoutRef.current !== null) {
        clearTimeout(persistTimeoutRef.current);
        persistReadingState();
      }
      scrollContainer?.removeEventListener('scroll', handleScroll);
    };
  }, [editor, documentId, mainAreaView, isDocumentLoaded]);

  useEffect(() => {
    if (mainAreaView !== null && editor && documentId && !isReadOnly) {
      const state: ReadingState = {
        pos: editor.state.selection.from,
        scrollTop: editorScrollContainerRef.current?.scrollTop ?? 0,
      };
      const signature = readingStateSignature(state);
      if (signature !== lastPersistedSignatureRef.current) {
        lastPersistedSignatureRef.current = signature;
        writeReadingStateToSessionStorage(documentId, state);
      }
      void readingStateSaverRef.current?.flush();
    }
  }, [mainAreaView, editor, documentId, isReadOnly]);

  useEffect(() => {
    const wasShowingPanel = previousMainAreaViewRef.current !== null;
    const nowShowingEditor = mainAreaView === null;
    if (wasShowingPanel && nowShowingEditor && editor && isDocumentLoaded && !isReadOnly && documentId) {
      const pendingCommentId = pendingEditorCommentScrollId ?? searchParams.get('commentId');
      const pendingComment = pendingCommentId
        ? comments.find((entry) => entry.id === pendingCommentId) ??
          deletedComments.find((entry) => entry.id === pendingCommentId)
        : null;
      if (pendingComment) {
        openCommentIdHandledRef.current = pendingComment.id;
        setPendingEditorCommentScrollId(null);
        scrollToCommentUtil(editor, pendingComment, editorScrollContainerRef);
      } else {
        const pendingSection = parseSectionScrollFromSearchParams(searchParams)
          ?? readPendingSectionScroll(documentId);
        if (!pendingSection) {
          restoreEditorScroll();
        }
      }
    }
    previousMainAreaViewRef.current = mainAreaView;
  }, [
    mainAreaView,
    editor,
    isDocumentLoaded,
    isReadOnly,
    documentId,
    restoreEditorScroll,
    searchParams,
    comments,
    deletedComments,
    pendingEditorCommentScrollId,
  ]);

  useEffect(() => {
    if (!editor || !documentId || mainAreaView !== null || !isDocumentLoaded) {
      return;
    }

    const pendingFromUrl = parseSectionScrollFromSearchParams(searchParams);
    const pendingSection = pendingFromUrl ?? readPendingSectionScroll(documentId);
    if (!pendingSection) {
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const tryScroll = (): void => {
      if (cancelled) {
        return;
      }

      const pos = findTocHeadingPosition(tocHeadings, pendingSection.title, pendingSection.level);
      if (pos !== null) {
        clearPendingSectionScroll(documentId);
        if (pendingFromUrl) {
          const nextParams = stripSectionScrollSearchParams(searchParams);
          setSearchParams(nextParams, { replace: true });
        }
        scrollToTocPosition(pos);
        return;
      }

      attempts += 1;
      if (attempts < 15) {
        setTimeout(tryScroll, 100);
      } else {
        clearPendingSectionScroll(documentId);
        if (pendingFromUrl) {
          const nextParams = stripSectionScrollSearchParams(searchParams);
          setSearchParams(nextParams, { replace: true });
        }
      }
    };

    tryScroll();

    return () => {
      cancelled = true;
    };
  }, [editor, documentId, mainAreaView, isDocumentLoaded, tocHeadings, scrollToTocPosition, searchParams, setSearchParams]);

  useEffect(() => {
    if (!editor) return;

    (window as any).checkHyphenToDashExtension = () => {
      const extension = editor.extensionManager.extensions.find((ext: any) => ext.name === 'hyphenToDash');
      return extension;
    };

    const updateWordCountAndReadingTime = (): void => {
      if (editor.isDestroyed) {
        return;
      }
      const text = editor.getText().trim();
      const count = countWords(text);
      setWordCount(count);
      setReadingTime(calculateReadingTime(count));
      setPageCount(estimatePages(count));

      const isEmptyDoc = text === '' || text === 'Start typing...';
      setIsEmpty(isEmptyDoc);
    };

    let initialWordCountTimeoutId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      initialWordCountTimeoutId = null;
      updateWordCountAndReadingTime();
    }, 0);

    const editorElement = getMountedEditorDom(editor);
    if (!editorElement) {
      if (initialWordCountTimeoutId !== null) {
        clearTimeout(initialWordCountTimeoutId);
      }
      return;
    }

    const handleFocus = (): void => {
      setIsEditorFocused(true);
    };

    const handleBlur = (): void => {
      setIsEditorFocused(false);
    };

    const checkFocus = (): void => {
      const activeElement = document.activeElement;
      const isFocused = editorElement.contains(activeElement) || editorElement === activeElement;
      setIsEditorFocused(isFocused);
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (isCommentModalOpenRef.current || isSynonymsPopupOpenRef.current) {
          return;
        }
        const { state } = editor.view;
        if (!state.selection.empty) {
          const { tr } = state;
          tr.setSelection(TextSelection.create(state.doc, state.selection.to));
          editor.view.dispatch(tr);
          event.preventDefault();
          event.stopPropagation();
        }
      }
    };

    const wordCountTimeoutRef: { current: ReturnType<typeof setTimeout> | null } = { current: null };
    const scheduleWordCountUpdate = (): void => {
      scheduleTrailingTimeout(wordCountTimeoutRef, updateWordCountAndReadingTime, EDITOR_IDLE_WORK_DEBOUNCE_MS);
    };

    editor.on('update', scheduleWordCountUpdate);

    editorElement.addEventListener('focus', handleFocus, true);
    editorElement.addEventListener('blur', handleBlur, true);
    editorElement.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', checkFocus);
    document.addEventListener('focusout', checkFocus);

    checkFocus();

    return () => {
      if (initialWordCountTimeoutId !== null) {
        clearTimeout(initialWordCountTimeoutId);
      }
      editor.off('update', scheduleWordCountUpdate);
      if (wordCountTimeoutRef.current !== null) {
        clearTimeout(wordCountTimeoutRef.current);
        updateWordCountAndReadingTime();
      }
      editorElement.removeEventListener('focus', handleFocus, true);
      editorElement.removeEventListener('blur', handleBlur, true);
      editorElement.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', checkFocus);
      document.removeEventListener('focusout', checkFocus);
    };
  }, [editor, isDocumentLoaded]);

  useEffect(() => {
    if (!editor) return;

    let hasNonEmptySelectionStats = false;

    const updateSelectedTextStats = (): void => {
      if (editor.isDestroyed) {
        return;
      }
      const { state } = editor;
      const { selection } = state;
      const { from, to } = selection;

      if (from === to) {
        if (hasNonEmptySelectionStats) {
          hasNonEmptySelectionStats = false;
          setSelectedTextWordCount(0);
          setSelectedTextReadingTime(0);
          setSelectedTextPageCount(0);
        }
        return;
      }

      try {
        const selectedText = state.doc.textBetween(from, to).trim();
        const count = countWords(selectedText);
        hasNonEmptySelectionStats = true;
        setSelectedTextWordCount(count);
        setSelectedTextReadingTime(calculateReadingTime(count));
        setSelectedTextPageCount(estimatePages(count));
      } catch (error) {
        hasNonEmptySelectionStats = false;
        setSelectedTextWordCount(0);
        setSelectedTextReadingTime(0);
        setSelectedTextPageCount(0);
      }
    };

    // Update on selection changes
    editor.on('selectionUpdate', updateSelectedTextStats);
    editor.on('transaction', updateSelectedTextStats);

    // Initial update
    updateSelectedTextStats();

    return () => {
      editor.off('selectionUpdate', updateSelectedTextStats);
      editor.off('transaction', updateSelectedTextStats);
    };
  }, [editor]);


  if (authLoading || !editor || !isDocumentLoaded) {
    return <DocumentLoadingPanel />;
  }

  if (mainAreaView && documentId) {
    const projectSectionLabel = ((): string | null => {
      switch (mainAreaView) {
        case 'characters':
          return t('characters');
        case 'locations':
          return t('locations');
        case 'storylines':
          return t('storylines');
        case 'scenes':
          return t('scenes');
        case 'chapters':
          return t('chapters');
        case 'analysis':
          return t('Analysis');
        default:
          return null;
      }
    })();

    if (projectSectionLabel) {
      const projectSectionContent = ((): React.ReactNode => {
        switch (mainAreaView) {
          case 'characters':
            return <Characters documentId={documentId} projectId={projectId || undefined} isSidebar={false} initialCharacterId={urlCharacterId ?? undefined} />;
          case 'locations':
            return <Locations documentId={documentId} projectId={projectId || undefined} isSidebar={false} initialLocationId={urlLocationId ?? undefined} />;
          case 'storylines':
            return <Storylines documentId={documentId} projectId={projectId || undefined} isSidebar={false} initialStorylineId={urlStorylineId ?? undefined} />;
          case 'scenes':
            return <Scenes documentId={documentId} projectId={projectId || undefined} isSidebar={false} initialSceneId={urlSceneId ?? undefined} />;
          case 'chapters':
            return <Chapters documentId={documentId} projectId={projectId || undefined} isSidebar={false} initialChapterId={urlChapterId ?? undefined} mode="outline" />;
          case 'analysis':
            return <Chapters documentId={documentId} projectId={projectId || undefined} isSidebar={false} initialChapterId={urlChapterId ?? undefined} mode="analysis" />;
          default:
            return null;
        }
      })();

      return (
        <SidebarInset style={{ overflow: 'hidden', height: APP_VIEWPORT_HEIGHT }}>
          <DocumentHeader
            documentTitle={documentTitle}
            projectTitle={projectTitle}
            documentIcon={documentIcon}
            onDocumentTitleChange={handleTitleChange}
            onProjectTitleChange={handleProjectTitleChange}
            onIconChange={handleIconChange}
            isReadOnly={isReadOnly}
            showFocusMode={false}
            isMainDocument={documentId === projectMainDocumentId}
            documents={projectDocuments}
            currentDocumentId={documentId}
            mainDocumentId={projectMainDocumentId}
            onSelectDocument={handleSelectDocument}
            onCreateDocument={() => {
              void handleCreateDocument();
            }}
            canCreateDocument={!isReadOnly && Boolean(projectId || documentProjectId)}
            sectionLabel={projectSectionLabel}
            sectionIcon={getProjectSectionIcon(mainAreaView)}
          />
          <div
            className={`editor-scroll-container ${PANEL_BODY_FILL_CLASSNAME}`}
            style={{
              overflow: mainAreaView === 'chapters' ? 'hidden' : 'auto',
              padding: mainAreaView === 'chapters' ? 0 : projectPanelPadding(isMobile),
            }}
          >
            {projectSectionContent}
          </div>
        </SidebarInset>
      );
    }
  }


  return (
    <>
      {CreditLimitModalComponent}
      <MobileAnalysisProgressToast
        isAnalyzing={isLoadingFeedback}
        shouldOpenSidebar={!isMobile}
      />
      <EditorRightSidebarProvider activeMainAreaView={mainAreaView} onSelectMainAreaView={handleSelectMainAreaView}>
        <HeadingSectionMenuContextSetup
          editor={editor}
          isAnalyzing={isLoadingFeedback}
          onAnalyzeSection={handleAnalyzeSection}
          onMoveToNote={projectId ? handleMoveToNote : null}
          projectId={projectId || documentProjectId || null}
          documentId={documentId && documentId !== 'new' ? documentId : null}
        >
        <SidebarCollapseHandler documentId={documentId} previousDocumentIdRef={previousDocumentIdRef} />
        <SidebarInset className="editor-main-inset" style={{ overflow: 'hidden', height: APP_VIEWPORT_HEIGHT }}>
      {!isReadOnly && !mainAreaView && (
        <FormattingBar
          editor={editor}
          projectId={projectId ?? null}
          documentId={documentId ?? null}
          onMoveToNote={handleMoveToNote}
          onCommentClick={() => {
            if (editor) {
              const { state } = editor;
              const { selection } = state;
              const { from, to } = selection;
              if (from !== to) {
                const selectedText = getDocumentPlainText(state.doc, from, to);
                setSelectedTextForComment(selectedText);
                setSelectedRangeForComment({ from, to });
                setIsCommentModalOpen(true);
              }
            }
          }}
          onAnalyzeClick={analyzeSelectedText}
          onSynonymsClick={handleSynonymsClick}
          isAnalyzing={isLoadingFeedback}
        />
      )}
      <SynonymsPopup
        isOpen={isSynonymsPopupOpen}
        position={synonymsPopupPosition}
        synonyms={synonyms}
        loading={isLoadingSynonyms}
        wordCount={synonymsWordCount}
        onSelect={handleSynonymSelect}
        onClose={() => {
          if (editor && selectedRangeForSynonyms) {
            editor.chain().setTextSelection({ from: selectedRangeForSynonyms.from, to: selectedRangeForSynonyms.to }).focus().run();
          }
          setIsSynonymsPopupOpen(false);
          setSelectedRangeForSynonyms(null);
        }}
      />
      <StyleCalibrationModal
        isOpen={isStyleCalibrationModalOpen}
        onClose={handleStyleCalibrationCancel}
        onAccept={handleStyleCalibrationAccept}
        pendingSuggestionsCount={pendingStyleCalibrationPendingCount}
      />
      <CommentModal
        isOpen={isCommentModalOpen}
        mentionableEntities={mentionableEntities}
        onClose={() => {
          const range = selectedRangeForComment;
          setIsCommentModalOpen(false);
          setSelectedTextForComment('');
          setSelectedRangeForComment(null);
          if (editor && range) {
            deferAfterDialogExit(() => {
              restoreEditorTextSelection(editor, range);
            });
          }
        }}
        onSubmit={async (content: string) => {
          if (!editor || !token || !documentId || documentId === 'new' || !selectedRangeForComment) {
            return;
          }
          setIsCreatingComment(true);
          try {
            const response = await fetch(`${API_BASE}/api/comments`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                documentId,
                content,
                fromPos: selectedRangeForComment.from,
                toPos: selectedRangeForComment.to,
                ...(encodeDocAnchor(
                  editor,
                  ydoc,
                  selectedRangeForComment.from,
                  selectedRangeForComment.to,
                ) ?? {}),
                selectedText: selectedTextForComment,
              }),
            });
            if (response.ok) {
              const range = selectedRangeForComment;
              setIsCommentModalOpen(false);
              setSelectedTextForComment('');
              setSelectedRangeForComment(null);
              await waitForDialogExit();
              if (!editor.isDestroyed) {
                restoreEditorTextSelection(editor, range);
              }
              await loadComments();
              if (refreshCommentsRef.current) {
                refreshCommentsRef.current();
              }
            } else {
              showErrorToast(t('failed_to_create_comment'));
            }
          } catch (err) {
            console.error('Error creating comment:', err);
            showErrorToast(t('failed_to_create_comment'));
          } finally {
            setIsCreatingComment(false);
          }
        }}
        selectedText={selectedTextForComment}
        isLoading={isCreatingComment}
      />
        <div style={{ display: 'flex', flexDirection: 'column', height: APP_VIEWPORT_HEIGHT, overflow: 'hidden', position: 'relative' }}>
        {!isFocusMode && (
          <DocumentHeader
            documentTitle={documentTitle}
            projectTitle={projectTitle}
            documentIcon={documentIcon}
            onDocumentTitleChange={handleTitleChange}
            onProjectTitleChange={handleProjectTitleChange}
            onIconChange={handleIconChange}
            isReadOnly={isReadOnly}
            onFocusMode={() => setIsFocusMode(true)}
            showFocusMode={mainAreaView === null}
            showRightSidebarToggle={true}
            isMainDocument={documentId != null && documentId === projectMainDocumentId}
            documents={projectDocuments}
            currentDocumentId={documentId}
            mainDocumentId={projectMainDocumentId}
            onSelectDocument={handleSelectDocument}
            onCreateDocument={() => {
              void handleCreateDocument();
            }}
            canCreateDocument={!isReadOnly && Boolean(projectId || documentProjectId)}
            sectionLabel={
              mainAreaView
                ? (() => {
                    const labelKey = getDocumentSectionLabelKey(mainAreaView);
                    return labelKey ? t(labelKey) : null;
                  })()
                : null
            }
            sectionIcon={mainAreaView ? getDocumentSectionIcon(mainAreaView) : undefined}
          />
        )}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row', position: 'relative' }}>
        <FloatingTocButton
          tocHeadings={tocHeadings}
          isFloatingTocVisible={isFloatingTocVisible}
          setIsFloatingTocVisible={setIsFloatingTocVisible}
          isTocPinned={isTocPinned}
          setIsTocPinned={setIsTocPinned}
          isMobile={isMobile}
          isFocusMode={isFocusMode}
          hasMainAreaView={mainAreaView !== null}
          t={t}
        />
        {!isFocusMode && mainAreaView && (
          <FloatingClosePanelButton onClick={handleCloseMainArea} ariaLabel={t('close')} />
        )}
        {!isFocusMode && (
          <FloatingSearchButton
            isSearchVisible={isSearchVisible}
            setIsSearchVisible={setIsSearchVisible}
            isMobile={isMobile}
            isFocusMode={isFocusMode}
            hasMainAreaView={mainAreaView !== null}
            t={t}
          />
        )}
        {isMobile && (
          <OfflineIndicator variant="floating" />
        )}
        <FloatingToc
          tocHeadings={tocHeadings}
          tocParagraphs={tocParagraphs}
          isFloatingTocVisible={isFloatingTocVisible}
          setIsFloatingTocVisible={setIsFloatingTocVisible}
          isTocPinned={isTocPinned}
          setIsTocPinned={setIsTocPinned}
          activeTocPos={activeTocPos}
          isMobile={isMobile}
          isReadOnly={isReadOnly}
          isFocusMode={isFocusMode}
          scrollToTocPosition={scrollToTocPosition}
          moveTocSection={moveTocSection}
          draggedTocIndex={draggedTocIndex}
          setDraggedTocIndex={setDraggedTocIndex}
          dragOverTocIndex={dragOverTocIndex}
          setDragOverTocIndex={setDragOverTocIndex}
          touchDraggedIndex={touchDraggedIndex}
          setTouchDraggedIndex={setTouchDraggedIndex}
          touchDragOverIndex={touchDragOverIndex}
          setTouchDragOverIndex={setTouchDragOverIndex}
          touchStartY={touchStartY}
          setTouchStartY={setTouchStartY}
          tocListRef={tocListRef}
          t={t}
        />
        <div
          ref={editorScrollContainerRef}
          className={`editor-scroll-container${mainAreaView ? ' bg-background' : ''}`}
          style={{ flex: 1, overflowX: 'hidden', overflowY: isEmpty ? 'visible' : 'auto', paddingTop: '0', paddingBottom: '50vh', paddingRight: '0', position: 'relative' }}
          onMouseDown={(event) => {
            handleMouseDownBelowEditorContent(
              editor,
              {
                clientY: event.clientY,
                target: event.target,
                preventDefault: () => {
                  event.preventDefault();
                },
              },
              {
                editable: !isReadOnly && mainAreaView === null,
              },
            );
          }}
        >
        {!isFocusMode && isSearchVisible && (
          <SearchBox
            editor={editor}
            isFocusMode={false}
            isMobile={isMobile}
            editorScrollContainerRef={editorScrollContainerRef}
            onClose={() => {
              setIsSearchVisible(false);
              setIsReplaceMode(false);
            }}
            isVisible={isSearchVisible}
            initialReplaceMode={isReplaceMode}
            initialSearchText={searchInitialText}
          />
        )}
        <div
          style={{
            maxWidth: mainAreaView ? '100%' : '900px',
            width: '100%',
            minWidth: 0,
            margin: mainAreaView ? '0' : '0 auto',
            paddingTop: isFocusMode ? '0' : (isMobile && mainAreaView ? '1rem' : '2rem'),
            paddingLeft: isFocusMode ? (isMobile ? '1.5rem' : '2.5rem') : (isMobile && mainAreaView ? '1rem' : '2rem'),
            paddingRight: isFocusMode ? (isMobile ? '1.5rem' : '2.5rem') : (isMobile && mainAreaView ? '1rem' : '2rem'),
            boxSizing: 'border-box',
          }}
        >
          {mainAreaView ? (
            <div style={{ minHeight: '100%' }}>
              {renderMainAreaContent()}
            </div>
          ) : null}
          <div style={{ display: mainAreaView ? 'none' : undefined }}>
              {isReadOnly && (
                <div
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#fef3c7',
                    border: '1px solid #fbbf24',
                    borderRadius: '0.5rem',
                    marginBottom: '1rem',
                    color: '#92400e',
                    fontSize: '0.875rem',
                  }}
                >
                  {shareToken
                    ? `${t('read_only_mode')}: ${t('document_shared_via_link')}`
                    : t('read_only_mode')}
                </div>
              )}

              <div
                onClick={(event) => {
                  if (!editor || editor.view.hasFocus()) {
                    return;
                  }
                  if (isPointerBelowEditorContent(editor, event.clientY)) {
                    focusEditorAtEndWithoutScrolling(editor);
                    return;
                  }
                  editor.commands.focus(undefined, { scrollIntoView: false });
                }}
                style={{
                  minHeight: isEmpty ? appViewportHeightMinus('200px') : 'auto',
                  width: '100%',
                  cursor: 'text',
                  padding: (() => {
                    if (isMobile) {
                      return isFocusMode
                        ? 'calc(1rem + 40px + 0.5rem) 0 0 0'
                        : '0 0 0 0';
                    }
                    if (isFocusMode) {
                      return '4rem 0 2rem 0';
                    }
                    return '2rem';
                  })(),
                  margin: '0',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}
              >
                <div ref={editorWrapperRef} style={{ flex: 1, width: '100%', position: 'relative' }}>
                  <EditorContent editor={editor} />
                  <SceneHeadingScriptInfo editor={editor} />
                  {!isReadOnly ? <ParagraphSectionMenu editor={editor} /> : null}
                  <DocumentSpeechPlaybackBar documentId={documentId} />
                </div>
              </div>
          </div>
        </div>
        </div>
        {!isFocusMode && !mainAreaView && (
          <EditorStatsOverlay
            wordCount={wordCount}
            readingTime={readingTime}
            pageCount={pageCount}
            selectedWordCount={selectedTextWordCount}
            selectedReadingTime={selectedTextReadingTime}
            selectedPageCount={selectedTextPageCount}
            t={t}
          />
        )}

        </div>
        </div>

      {isFocusMode && (
        <>
          <button
            className="floating-editor-control"
            onClick={() => setIsFocusMode(false)}
            style={{
              position: 'fixed',
              top: '1rem',
              right: isMobile ? '1.6rem' : '1rem',
              zIndex: 50,
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: 'none',
              padding: 0,
              color: 'var(--muted-foreground)',
              transition: 'background-color 0.2s, box-shadow 0.2s',
            }}
            aria-label={t('exit_focus_mode') || 'Exit focus mode'}
            title={t('exit_focus_mode') || 'Exit focus mode'}
          >
            <Minimize className="h-5 w-5" style={{ color: 'currentColor' }} />
          </button>
          <FloatingSearchButton
            isSearchVisible={isSearchVisible}
            setIsSearchVisible={setIsSearchVisible}
            isMobile={isMobile}
            isFocusMode={isFocusMode}
            hasMainAreaView={mainAreaView !== null}
            t={t}
          />
          {isSearchVisible && (
            <SearchBox
              editor={editor}
              isFocusMode={true}
              isMobile={isMobile}
              editorScrollContainerRef={editorScrollContainerRef}
              onClose={() => {
                setIsSearchVisible(false);
                setIsReplaceMode(false);
              }}
              isVisible={isSearchVisible}
              initialReplaceMode={isReplaceMode}
              initialSearchText={searchInitialText}
            />
          )}
        </>
      )}

      <ScriptModeFloatingButton
        isVisible={isScriptModeActive}
        onExit={() => {
          canAutoActivateScriptModeFromSceneRef.current = false;
          setIsScriptModeActive(false);
        }}
        isMobile={isMobile}
        isFocusMode={isFocusMode}
        t={t}
      />

        <SuggestionHoverCard
          hoveredSuggestion={hoveredSuggestion}
          hoveredSuggestionPosition={hoveredSuggestionPosition}
          suggestionHoverCardContentRef={suggestionHoverCardContentRef}
          isMobile={isMobile}
          onClose={closeSuggestionHover}
          onHoverCardMouseEnter={onSuggestionHoverCardMouseEnter}
          onHoverCardMouseLeave={onSuggestionHoverCardMouseLeave}
          onApplySuggestion={handleApplySuggestion}
          onRejectSuggestion={handleRejectSuggestion}
          onDismissSuggestion={handleDismissSuggestion}
          t={t}
          getIssueTypeLabel={getIssueTypeLabel}
        />
        <CommentHoverCard
          editor={editor}
          hoveredComment={hoveredComment}
          hoveredCommentPosition={hoveredCommentPosition}
          commentHoverCardContentRef={commentHoverCardContentRef}
          isMobile={isMobile}
          documentOwnerId={documentOwnerId}
          onLoadComments={syncCommentLists}
          onClose={closeCommentHover}
          onHoverCardMouseEnter={onCommentHoverCardMouseEnter}
          onHoverCardMouseLeave={onCommentHoverCardMouseLeave}
          setKeepCommentHoverOpen={setKeepCommentHoverOpen}
          scrollContainerRef={editorScrollContainerRef}
          setHoveredComment={setHoveredComment}
          mentionableEntities={mentionableEntities}
        />
      </SidebarInset>
      {!isFocusMode && (
        <EditorRightSidebar
          editor={editor}
          suggestions={suggestions}
          onLoadSuggestions={loadSuggestions}
          onTriggerAnalysis={triggerNewReview}
          isAnalyzing={isLoadingFeedback}
          onHighlightSuggestions={highlightSuggestions}
          onClearSuggestionHighlights={clearSuggestionHighlights}
          onScrollToSuggestion={scrollToSuggestionInDocument}
          onRegisterShowSuggestions={(openSuggestions) => {
            showSuggestionsRef.current = openSuggestions;
            if (!openSuggestionsHandledRef.current && searchParams.get('openSuggestions') === '1') {
              openSuggestionsHandledRef.current = true;
              openSuggestions();
            }
          }}
          onRegisterShowComments={(openComments) => {
            showCommentsRef.current = openComments;
          }}
          onRegisterRefreshComments={(refreshComments) => {
            refreshCommentsRef.current = refreshComments;
          }}
          isFocusMode={isFocusMode}
          comments={comments}
          onLoadComments={loadComments}
          documentOwnerId={documentOwnerId}
          mentionableEntities={mentionableEntities}
          canManageSharing={canManageSharing}
          onApplySuggestion={handleApplySuggestion}
          onRejectSuggestion={handleRejectSuggestion}
          onDismissSuggestion={handleDismissSuggestion}
          onDismissAllSuggestions={handleDismissAllSuggestions}
          isLoadingSuggestions={false}
          tocHeadings={tocHeadings}
          tocParagraphs={tocParagraphs}
          onScrollToTocPosition={scrollToTocPosition}
          onMoveTocSection={moveTocSection}
          draggedTocIndex={draggedTocIndex}
          setDraggedTocIndex={setDraggedTocIndex}
          dragOverTocIndex={dragOverTocIndex}
          setDragOverTocIndex={setDragOverTocIndex}
          touchDraggedIndex={touchDraggedIndex}
          setTouchDraggedIndex={setTouchDraggedIndex}
          touchDragOverIndex={touchDragOverIndex}
          setTouchDragOverIndex={setTouchDragOverIndex}
          touchStartY={touchStartY}
          setTouchStartY={setTouchStartY}
          tocListRef={tocListRef}
          isReadOnly={isReadOnly}
          projectId={projectId}
          documentId={documentId}
          shareToken={shareToken}
          token={token}
          showErrorToast={showErrorToast}
          showToast={showToast}
          t={t}
          ydoc={ydoc}
          documentTitle={documentTitle}
          projectTitle={projectTitle}
          documentProjectId={documentProjectId}
          wordCount={wordCount}
          readingTime={readingTime}
          pageCount={pageCount}
          selectedWordCount={selectedTextWordCount}
          selectedReadingTime={selectedTextReadingTime}
          selectedPageCount={selectedTextPageCount}
        />
      )}
      <SuggestionNotFoundModal
        open={suggestionNotFound !== null}
        originalText={suggestionNotFound?.originalText ?? ''}
        onOpenChange={(open) => {
          if (!open) {
            setSuggestionNotFound(null);
          }
        }}
        onDismissSuggestion={() => {
          if (suggestionNotFound) {
            void handleDismissSuggestion(suggestionNotFound);
          }
        }}
        t={t}
      />
        </HeadingSectionMenuContextSetup>
      </EditorRightSidebarProvider>
    </>
  );
};

export default Editor;
